import { AnimatePresence, motion, type PanInfo, type Variants } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { arrayRemove, arrayUnion, collection, deleteField, doc, increment, onSnapshot, query, setDoc, updateDoc, where } from 'firebase/firestore'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import UndoRoundedIcon from '@mui/icons-material/UndoRounded'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded'
import type {
  ChecklistComment,
  ChecklistItem as BoardChecklistItem,
  ChecklistMemberOption,
  ChecklistWorkflowStatus,
} from '../../types/schema'
import { firestoreDb } from '../../services/firebase'
import { writeChecklistUpdate } from '../../services/checklistWriter'
import {
  isChecklistDetailVersionedField,
  isChecklistRecurringGlobalField,
} from '../../services/checklistDetailVersionService'
import { queryWorkspaceActivityPage } from '../../services/activityService'
import { useAuth } from '../../context/useAuth'
import { useDisplayName } from '../../hooks/useDisplayName'
import { useWorkspace } from '../../context/useWorkspace'
import { roleCanPerform } from '../../utils/workspacePermissions'
import { EntityFormDialog } from '../Common/EntityFormDialog'
import { GoalClientPicker } from './GoalClientPicker'
import { getBudgetExpenseFormSections } from '../Budget/budgetExpenseFormSections'
import { createBudgetRecurringRule } from '../../services/budgetRecurringService'

import { useToast } from '../../context/useToast'
import { useEditLock } from '../../hooks/useEditLock'
import { isManualSaveField } from '../../services/checklistEditFieldPolicy'
import { useClientPreferences } from '../../context/useClientPreferences'
import { useNls } from '../../nls/nlsProvider'
import { normalizeChecklistInputLimits, truncateToLimit } from '../../utils/checklistLimits'
import { isPersonalWorkspace } from '../../utils/workspaceKind'
import { resolveProfileImageUrl } from '../../utils/profileImage'
import {
  getAdaptiveSheetVariants,
  MOBILE_SHEET_DRAG_LIMIT_PX,
  shouldDismissMobileSheet,
} from '../../utils/adaptiveSheetMotion'
import {
  DependencyLinkList,
  StatusSelect,
} from './NodeDetailControls'
import { StatusBlockerBanner } from './StatusBlockerBanner'
import {
  canPickInDropdown,
  type BlockerEntry,
} from '../../services/checklistStatusMachine'
import {
  type BlockerCtaSection,
} from '../../types/checklistBlockerCodes'
import {
  ChecklistNodeCompletionCriteriaField,
  ChecklistNodeCoreFields,
  ChecklistNodeDescriptionField,
  ChecklistNodeRiskSection,
  ChecklistNodeTitleField,
} from './ChecklistNodeDetailFields'
import {
  getLifecycleChip,
  getOpenBlockerTitles,
  getRatingOptionLabel,
  getWorkflowStatus,
  RATING_DIMENSIONS,
  type RatingDimensionKey,
} from '../../utils/checklistNodeMeta'
import { AppDialog } from '../Common/AppDialog'
import { SettingsRadioList } from '../Common/SettingsRadioList'
import { ControlNotice } from '../Common/ControlNotice'
import { MiniLoader } from '../Common/MiniLoader'
import { Button, ConfirmActionModal, CountBadge, HiddenFileInput, Radio, Select, StatusChip, type StatusPillTone, Textarea, TextInput, Toggle } from '../Common'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { ItemTimerButton } from '../Timesheets/ItemTimerButton'
import { ItemTimeDisplay } from '../Timesheets/ItemTimeDisplay'
import { ItemTimeProvider } from '../Timesheets/ItemTimeProvider'
import { ItemAttachmentsPanel } from '../Forms/ItemAttachmentsPanel'
import { ChecklistFieldPassControl } from '../FieldPass/ChecklistFieldPassControl'
import { NodeStockTab } from '../Stock/NodeStockTab'
// Gating bypassed for testing — re-wire isStockEnabled later.
import { syncStockTaskLinks } from '../../services/stockService'
import { ResourceBadges } from '../Resources/ResourceBadges'
import { buildResourceImpactPreview, buildResourceSignals, normalizeStockRequirements, stockRequirementIds } from '../../services/resourceAdapter'
import { recordWorkspaceActivity } from '../../services/activityService'
import { vouchForChecklistCompletion } from '../../services/reliabilityService'
import {
  isBudgetEnabled as isBudgetEnabledForWorkspace,
  canMutateBudget as canMutateBudgetForRole,
  canViewStock as canViewStockForRole,
} from '../../services/featureRegistry'
import { buildBudgetEntityChain } from '../../services/budgetEntityChain'
import {
  logExpense,
  reverseExpense,
  setBudgetLimit as setBudgetLimitForEntity,
  subscribeEntityExpenses,
  BudgetCurrencyMismatchError,
  BudgetModuleDisabledError,
} from '../../services/budgetService'
import {
  detachProvisionFromItem,
  deleteWorkspaceProvisionPermanently,
} from '../../services/itemAttachmentService'
import {
  BUDGET_CATEGORIES,
  DEFAULT_BUDGET_CATEGORY,
  WORKSPACE_EXPENSES_COLLECTION,
  computeBudgetBand,
  type BudgetCategory,
  type BudgetProgressReference,
  type WorkspaceExpense,
} from '../../types/budget'
import {
  MAX_CHECKLIST_COMMENT_ATTACHMENTS,
  uploadChecklistCommentAttachments,
  uploadChecklistProof,
} from '../../services/checklistAttachmentService'
import { buildChecklistProofRemovalUpdates } from '../../services/checklistProofService'
import { buildProofSubmissionEffects, submitRepeatingProofEffects } from '../../services/checklistProofSubmissionEffects'
import { resolveBlockedByLinks, resolveBlockerForLinks } from '../../utils/checklistDependencyLinks'
import {
  CARD_BACKGROUND_CATEGORY_LABEL,
  clearChecklistCardBackgroundUpdates,
  uploadChecklistCardBackground,
} from '../../services/cardBackgroundService'
import { isNoSignInPreviewActive } from '../../debug'
import { ChecklistBookChip, splitBookSegment } from '../ChecklistBoard/ChecklistBreadcrumb'
import { resolveDefaultBreadcrumb, type ChecklistBreadcrumbSegment } from '../../utils/checklistBreadcrumb'
import { CHECKLIST_DETAIL_SECTIONS, sectionLabel, type ChecklistDetailSectionId } from '../../config/checklistDetailSections'
import { useTimeline } from '../../context/useTimeline'
import { NodeDetailLocationBreadcrumb } from './NodeDetailLocationBreadcrumb'
import { NodeDetailCommentCard } from './NodeDetailCommentCard'
import {
  getNodeDetailTabDomIds,
  NodeDetailTabStrip,
  type NodeDetailTab,
} from './NodeDetailTabStrip'
import { ChecklistPanel, type ChecklistPanelSection } from '../ChecklistBoard/ChecklistPanel'
import { ExternalLinkSidecard } from '../LinkHub/shared/ExternalLinkSidecard'

type NodeDetailPresentation = 'drawer' | 'popup' | 'inline'

const popupSheetVariants: Variants = {
  hidden: { opacity: 0, y: 96, scale: 0.96 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.24, ease: 'easeOut' } },
  exit: { opacity: 0, y: 88, scale: 0.97, transition: { duration: 0.18, ease: 'easeIn' } },
}

export type NodeDetailDrawerProps = {
  isOpen: boolean
  node: BoardChecklistItem | null
  presentation?: NodeDetailPresentation
  boardNodes: BoardChecklistItem[]
  workspaceMembers: ChecklistMemberOption[]
  isLocked?: boolean
  isReadOnly: boolean
  /** Whether the current user may edit (ADMIN/EDITOR). Drives the Edit toggle. */
  canEdit: boolean
  /** Whether the current user may delete this node. */
  canDelete: boolean
  /** Permanently delete this node. Editor/admin only (parent enforces). */
  onDeleteNode: (node: BoardChecklistItem) => Promise<void>
  /** Brand-new draft mode (Manage panel "+ Add Item"). When true: the drawer
   * auto-enters Edit, every change accumulates in the local draft (no
   * Firestore writes), Save requires a non-empty title, and Close shows a
   * "Discard this new item?" confirmation. */
  isNewNode?: boolean
  /** Commit the new node (called only when isNewNode is true). Receives the
   * fully-merged draft so the parent can persist it as a single create. */
  onCreate?: (node: BoardChecklistItem) => Promise<void>
  /** Optional Save button label for hosts that create a specialized new item. */
  newNodeSaveLabel?: string
  onClose: () => void
  onTitleBlur: (node: BoardChecklistItem, title: string) => Promise<void>
  onWeightageBlur: (node: BoardChecklistItem, weightage: string) => Promise<void>
  onSaveDraft?: (
    node: BoardChecklistItem,
    draft: Partial<BoardChecklistItem>,
    opts?: { scope?: 'single' | 'future' | 'global' | null },
  ) => Promise<void>
  onVerificationChange: (node: BoardChecklistItem, updates: Partial<BoardChecklistItem>) => Promise<void>
  onStatusChange: (
    node: BoardChecklistItem,
    status: ChecklistWorkflowStatus,
    opts?: { notRequiredScope?: 'FROM_PERIOD' | 'CLEAR' | null },
  ) => Promise<void>
  onAssignmentChange: (
    node: BoardChecklistItem,
    assignee: ChecklistMemberOption | null,
    opts?: { scope?: 'single' | 'future' | 'global' | null; pageKey?: string | null },
  ) => Promise<void>
  operationalPermissions: {
    assign: boolean
    status: boolean
    complete: boolean
    proof: boolean
    memo: boolean
    comment: boolean
    locationCheckin: boolean
  }
  operationalDisabledReason?: string | null
  completionDisabledReason?: string | null
  /** Location shown in the popup header. Callers supply their current-view
   *  breadcrumb so the popup reflects where the user opened it from. When
   *  omitted, a fallback path is derived from the item's parent chain. */
  breadcrumb?: ChecklistBreadcrumbSegment[] | null
  /** Optional reparent hook — when provided, the breadcrumb crumbs render as
   *  buttons that open a rule-aware parent picker. Returning `needs-clamp`
   *  from the callback triggers a "Clamp checklist to parent window" fix. */
  onReparent?: (
    nextParentId: string | null,
    opts?: { window?: { start: string; end: string } | null },
  ) => Promise<import('../../types/checklistDetailController').ChecklistParentSaveOutcome>
  /** Candidate GOAL/TASK/SUBTASK elements the parent picker lists. */
  parentPickerCandidates?: import('../../types/timelineElement').TimelineElement[]
  /** Workspace-default container id — used to represent the "no parent" row. */
  parentPickerWorkspaceDefaultId?: string
  /** When set on a recurring master (DAILY / WEEKLY), completion display
   *  reads the per-occurrence state from `node.occurrences[key]` instead of
   *  the master `isCompleted` mirror. Master fields (title, weight, risk,
   *  attachments etc.) are unaffected — that's the "main data from cover"
   *  contract. Ignored on INDIVIDUAL items. */
  focusedOccurrenceKey?: string | null
  pendingStatus?: ChecklistWorkflowStatus | null
  pendingBlockers?: BlockerEntry[]
  onPendingStatusDismiss?: () => void
}

const normalizeWeightage = (weightage: unknown) =>
  typeof weightage === 'number' && Number.isFinite(weightage) && weightage > 1
    ? Math.round(weightage)
    : 1

export const NodeDetailDrawer = ({
  isOpen,
  node,
  presentation = 'drawer',
  boardNodes,
  workspaceMembers,
  isLocked = false,
  isReadOnly,
  canEdit,
  canDelete,
  onDeleteNode,
  isNewNode,
  onCreate,
  newNodeSaveLabel,
  onClose,
  onTitleBlur,
  onWeightageBlur,
  onSaveDraft,
  onVerificationChange,
  onStatusChange,
  onAssignmentChange,
  operationalPermissions,
  operationalDisabledReason,
  completionDisabledReason,
  breadcrumb,
  onReparent,
  parentPickerCandidates,
  parentPickerWorkspaceDefaultId,
  focusedOccurrenceKey,
  pendingStatus = null,
  pendingBlockers = [],
  onPendingStatusDismiss,
}: NodeDetailDrawerProps) => {
  if (!node) {
    return <AnimatePresence>{false}</AnimatePresence>
  }

  const detailSheet = (
    <AnimatePresence>
      {isOpen && (
        <ItemTimeProvider workspaceId={node.workspaceId}>
          <NodeDetailDrawerContent
            key={node.id}
            node={node}
            presentation={presentation}
            boardNodes={boardNodes}
            workspaceMembers={workspaceMembers}
            isLocked={isLocked}
            isReadOnly={isReadOnly}
            canEdit={canEdit}
            canDelete={canDelete}
            onDeleteNode={onDeleteNode}
            isNewNode={isNewNode}
            onCreate={onCreate}
            newNodeSaveLabel={newNodeSaveLabel}
            onClose={onClose}
            onTitleBlur={onTitleBlur}
            onWeightageBlur={onWeightageBlur}
            onSaveDraft={onSaveDraft}
            onVerificationChange={onVerificationChange}
            onStatusChange={onStatusChange}
            onAssignmentChange={onAssignmentChange}
            operationalPermissions={operationalPermissions}
            operationalDisabledReason={operationalDisabledReason}
            completionDisabledReason={completionDisabledReason}
            breadcrumb={breadcrumb}
            onReparent={onReparent}
            parentPickerCandidates={parentPickerCandidates}
            parentPickerWorkspaceDefaultId={parentPickerWorkspaceDefaultId}
            focusedOccurrenceKey={focusedOccurrenceKey}
            pendingStatus={pendingStatus}
            pendingBlockers={pendingBlockers}
            onPendingStatusDismiss={onPendingStatusDismiss}
          />
        </ItemTimeProvider>
      )}
    </AnimatePresence>
  )

  if (presentation === 'popup' && typeof document !== 'undefined') {
    return createPortal(detailSheet, document.body)
  }

  // Inline presentation renders in the parent flow — no portal, no scrim.
  return detailSheet
}

type NodeDetailDrawerContentProps = Omit<NodeDetailDrawerProps, 'isOpen' | 'node'> & {
  node: BoardChecklistItem
}

const NodeDetailDrawerContent = ({
  node,
  presentation = 'drawer',
  boardNodes,
  workspaceMembers,
  isLocked = false,
  isReadOnly,
  canEdit,
  canDelete,
  onDeleteNode,
  isNewNode = false,
  onCreate,
  newNodeSaveLabel,
  onClose,
  onTitleBlur,
  onWeightageBlur,
  onSaveDraft,
  onVerificationChange,
  onStatusChange,
  onAssignmentChange,
  operationalPermissions,
  operationalDisabledReason,
  completionDisabledReason,
  breadcrumb,
  onReparent,
  parentPickerCandidates,
  parentPickerWorkspaceDefaultId,
  focusedOccurrenceKey,
  pendingStatus = null,
  pendingBlockers = [],
  onPendingStatusDismiss,
}: NodeDetailDrawerContentProps) => {
  const { currentUser, userProfile } = useAuth()
  const { showToast } = useToast()
  const { t } = useNls()
  const currentDisplayName = useDisplayName(userProfile ?? currentUser)
  const currentProfileImageUrl = resolveProfileImageUrl(userProfile, currentUser)
  const { activeRole, activeWorkspaceId, workspaces, canPerform } = useWorkspace()
  const { isMobileViewport } = useClientPreferences()
  const isDebugMode = isNoSignInPreviewActive()
  const activeWorkspace = workspaces.find(
    (workspace) => workspace.id === activeWorkspaceId || workspace.id === node.workspaceId,
  )
  const checklistLimits = normalizeChecklistInputLimits(
    activeWorkspace?.checklistInputLimits,
    isPersonalWorkspace(activeWorkspace),
  )
  const isPopupPresentation = presentation === 'popup'
  const isInlinePresentation = presentation === 'inline'
  const sheetVariants = isInlinePresentation
    ? undefined
    : isPopupPresentation
      ? popupSheetVariants
      : getAdaptiveSheetVariants(isMobileViewport)
  const handleSheetDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (!isPopupPresentation && !isInlinePresentation && shouldDismissMobileSheet(isMobileViewport, info)) onClose()
  }

  // Per-occurrence overlay: when a recurring master is shown scoped to a
  // specific date/week, completion-facing fields read from the item's
  // occurrences map; everything else (title / weight / risk / attachments /
  // etc.) still reads from the master. This is the "main data from cover,
  // completion swapped datewise" contract the recurring popup relies on.
  const activeOccurrenceState = focusedOccurrenceKey
    ? node.occurrences?.[focusedOccurrenceKey] ?? null
    : null
  const displayIsCompleted = focusedOccurrenceKey
    ? Boolean(activeOccurrenceState?.isCompleted)
    : node.isCompleted
  void displayIsCompleted // exposed for future header override; writes still go through the master item.

  const { elements, canPerformOnNode } = useTimeline()
  const candidateElements = (elements && elements.length > 0) ? elements : (parentPickerCandidates ?? [])
  const resolvedBreadcrumb: ChecklistBreadcrumbSegment[] = breadcrumb && breadcrumb.length > 0
    ? breadcrumb
    : resolveDefaultBreadcrumb(node, elements)

  const isViewer = activeRole === 'VIEWER'
  const canManageFieldPasses = canPerform('workspace.manage_field_passes')

  const [ratingToast, setRatingToast] = useState<string | null>(null)
  const ratingToastTimerRef = useRef<number | null>(null)
  const flashRatingToast = useCallback((message: string) => {
    setRatingToast(message)
    if (ratingToastTimerRef.current !== null) window.clearTimeout(ratingToastTimerRef.current)
    ratingToastTimerRef.current = window.setTimeout(() => setRatingToast(null), 1800)
  }, [])

  const editLock = useEditLock(isNewNode ? null : (node?.id ?? null), {
    workspaceId: node.workspaceId ?? null,
    actor: currentUser ? { uid: currentUser.uid, displayName: currentDisplayName || currentUser.email || 'A user' } : null,
    isAdmin: roleCanPerform(activeRole, 'ADMIN'),
    onLockConflict: (holder) => {
      showToast(`${holder} is editing this item.`, 'info')
      flashRatingToast(`${holder} is editing it`)
    },
  })

  const withLock = useMemo(() => {
    return <Args extends unknown[]>(fn: (...args: Args) => void | Promise<void>) => {
      return async (...args: Args): Promise<void> => {
        const ok = await editLock.acquireOnEdit()
        if (!ok) return
        await fn(...args)
        editLock.markActivity()
      }
    }
  }, [editLock])

  const [titleDraft, setTitleDraft] = useState(node.title)
  const [comments, setComments] = useState<ChecklistComment[]>([])
  const [commentDraft, setCommentDraft] = useState('')
  const [commentAttachmentFiles, setCommentAttachmentFiles] = useState<File[]>([])
  const [isCommentSending, setIsCommentSending] = useState(false)
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null)
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [activityEntries, setActivityEntries] = useState<Array<{
    id: string
    actorName: string
    summary: string
    createdAt: string
  }>>([])
  // ---- Budget / Financials state ------------------------------------------
  const [expenses, setExpenses] = useState<WorkspaceExpense[]>([])
  const [expenseCategory] = useState<BudgetCategory>(() => {
    try {
      const saved = window.localStorage.getItem('timelining:last-budget-category')
      return (BUDGET_CATEGORIES as readonly string[]).includes(saved ?? '') ? saved as BudgetCategory : DEFAULT_BUDGET_CATEGORY
    } catch {
      return DEFAULT_BUDGET_CATEGORY
    }
  })
  const [progressReference, setProgressReference] = useState<BudgetProgressReference>('budgetLimit')
  const [financialsBusy, setFinancialsBusy] = useState(false)
  const [financialsNotice, setFinancialsNotice] = useState<string | null>(null)
  const [budgetLimitDraft, setBudgetLimitDraft] = useState<string>('')
  const [pendingDeleteAttachment, setPendingDeleteAttachment] = useState<{ entry: WorkspaceExpense; attId: string } | null>(null)
  const [showAllExpenses, setShowAllExpenses] = useState(false)
  const [locationNotice, setLocationNotice] = useState<string | null>(null)
  const [permissionNotice, setPermissionNotice] = useState<string | null>(null)
  const [vouchNotice, setVouchNotice] = useState<string | null>(null)
  const [vouchBusy, setVouchBusy] = useState(false)
  const [nowMs, setNowMs] = useState(() => Date.now())
  const [isVideoRecorderOpen, setIsVideoRecorderOpen] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [cardBackgroundBusy, setCardBackgroundBusy] = useState(false)
  const [recordingStream, setRecordingStream] = useState<MediaStream | null>(null)
  const [isAudioRecording, setIsAudioRecording] = useState(false)
  // Deferred draft of the editable fields.
  const [draft, setDraft] = useState<Partial<BoardChecklistItem>>({})
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [closeDialogOpen, setCloseDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [titleScopeDialogOpen, setTitleScopeDialogOpen] = useState(false)
  const [recurringScopeDialogOpen, setRecurringScopeDialogOpen] = useState(false)
  const [saveAndCloseAfterConfirm, setSaveAndCloseAfterConfirm] = useState(false)
  // Recurring assignee change: writes are deferred until the user picks a
  // scope (this occurrence / this and future / every occurrence). Mirrors the
  // mobile MemberChecklistDetailPage pending-assignee flow.
  const [pendingRecurringAssignee, setPendingRecurringAssignee] = useState<{
    assignee: ChecklistMemberOption | null
  } | null>(null)
  const [notNeededScopeDialogOpen, setNotNeededScopeDialogOpen] = useState(false)
  const [notNeededScopeChoice, setNotNeededScopeChoice] = useState<'THIS_PERIOD' | 'FROM_PERIOD'>('THIS_PERIOD')
  const audioRecorderRef = useRef<MediaRecorder | null>(null)
  const audioStreamRef = useRef<MediaStream | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const videoPreviewRef = useRef<HTMLVideoElement | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordedChunksRef = useRef<Blob[]>([])
  const tabPanelRef = useRef<HTMLDivElement | null>(null)
  const commentFileInputRef = useRef<HTMLInputElement | null>(null)
  const proofFileInputRef = useRef<HTMLInputElement | null>(null)
  const bgFileInputRef = useRef<HTMLInputElement | null>(null)
  const [activeTabId, setActiveTabId] = useState<ChecklistDetailSectionId>('details')
  const [discussionSheetOpen, setDiscussionSheetOpen] = useState(false)

  const assignedHelper = node.assigneeId
    ? workspaceMembers.find((member) => member.userId === node.assigneeId) ?? null
    : null
  const canVouchForAssignee = Boolean(
    currentUser
    && node.assigneeId
    && currentUser.uid !== node.assigneeId
    && activeRole !== 'HELPER',
  )

  const handleVouch = withLock(async () => {
    if (!canVouchForAssignee) return
    setVouchBusy(true)
    setVouchNotice(null)
    try {
      const result = await vouchForChecklistCompletion(node.id)
      const rounded = Math.round(result.reliabilityScore)
      setVouchNotice(`Vouch recorded. Reliability is now ${rounded}/100.`)
      flashRatingToast('Peer vouch recorded.')
    } catch (error) {
      setVouchNotice(error instanceof Error ? error.message : 'Could not record this vouch.')
    } finally {
      setVouchBusy(false)
    }
  })

  // Live subscription on the checklist node document so external writes
  // (e.g. form/design provisions attached via itemAttachmentService) bubble back.
  const [liveChecklistDoc, setLiveChecklistDoc] = useState<Partial<BoardChecklistItem> | null>(null)
  useEffect(() => {
    if (isDebugMode || isNewNode || !node?.id) return
    const docRef = doc(firestoreDb, 'checklists', node.id)
    return onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        setLiveChecklistDoc(snapshot.data() as Partial<BoardChecklistItem>)
      }
    }, (error) => {
      console.warn('[NodeDetailDrawer] failed to subscribe to checklist doc', error)
    })
  }, [isDebugMode, isNewNode, node?.id])

  // Live comment feed for this node.
  useEffect(() => {
    if (isDebugMode) {
      setComments([])
      return
    }
    const effectiveWorkspaceId = node.workspaceId || activeWorkspaceId
    const commentsQuery = query(
      collection(firestoreDb, 'checklist_comments'),
      where('workspaceId', '==', effectiveWorkspaceId),
      where('checklistItemId', '==', node.id),
    )

    return onSnapshot(commentsQuery, (snapshot) => {
      setComments(
        snapshot.docs
          .map((snapshotDocument): ChecklistComment => {
            const data = snapshotDocument.data() as Partial<ChecklistComment>
            return {
              id: snapshotDocument.id,
              userId: typeof data.userId === 'string' ? data.userId : '',
              userName: typeof data.userName === 'string' ? data.userName : 'Teammate',
              userAvatarUrl: typeof data.userAvatarUrl === 'string' ? data.userAvatarUrl : undefined,
              text: typeof data.text === 'string' ? data.text : '',
              timestamp: typeof data.timestamp === 'string' ? data.timestamp : new Date(0).toISOString(),
              parentCommentId: typeof data.parentCommentId === 'string' ? data.parentCommentId : null,
              mentionedUserIds: Array.isArray(data.mentionedUserIds) ? data.mentionedUserIds.filter((id): id is string => typeof id === 'string') : [],
              mentionedUsers: Array.isArray(data.mentionedUsers) ? data.mentionedUsers : [],
              reactions: data.reactions && typeof data.reactions === 'object' ? data.reactions : {},
              editedAt: typeof data.editedAt === 'string' ? data.editedAt : null,
              deletedAt: typeof data.deletedAt === 'string' ? data.deletedAt : null,
              deletedBy: typeof data.deletedBy === 'string' ? data.deletedBy : null,
              pageKey: typeof (data as { pageKey?: unknown }).pageKey === 'string' ? (data as { pageKey: string }).pageKey : null,
              attachments: Array.isArray(data.attachments) ? data.attachments : [],
            }
          })
          .filter((comment) => !focusedOccurrenceKey || comment.pageKey === focusedOccurrenceKey)
          .filter((comment) => comment.text.trim() || comment.deletedAt || (comment.attachments?.length ?? 0) > 0)
          .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp)),
      )
    }, (error) => {
      if ((error as { code?: string })?.code === 'permission-denied') {
        console.debug('[NodeDetailDrawer] comment subscription permission-denied', node.id, focusedOccurrenceKey ?? 'all')
      } else {
        console.warn('[NodeDetailDrawer] comment subscription failed', error)
      }
      setComments([])
    })
  }, [focusedOccurrenceKey, isDebugMode, node.id, node.workspaceId, activeWorkspaceId])

  useEffect(() => {
    if (isDebugMode) {
      setActivityEntries([])
      return
    }
    let cancelled = false
    const now = new Date()
    void queryWorkspaceActivityPage(firestoreDb, node.workspaceId, { from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000), to: now, memberIds: [], itemIds: [node.id], activityTypes: [] }, null, 100)
      .then((result) => {
        if (cancelled) return
        setActivityEntries(result.records.map((record) => ({
          id: record.id,
          actorName: record.actorName || 'Teammate',
          summary: record.summary || 'Updated this checklist item',
          createdAt: record.createdAt?.toISOString() ?? '',
        })))
      })
      .catch(() => {
        if (!cancelled) setActivityEntries([])
      })
    return () => { cancelled = true }
  }, [isDebugMode, node.id, node.workspaceId])

  // ---- Budget realtime feed (direct expenses on this leaf only) -----------
  const budgetEnabled = isBudgetEnabledForWorkspace(activeWorkspace ?? null)
  const canMutateFinancials = canMutateBudgetForRole(activeWorkspace ?? null, activeRole ?? null) && (canPerformOnNode ? canPerformOnNode('board.edit', node.id) : true)
  const canViewStockRequirements = canViewStockForRole(activeWorkspace ?? null, activeRole ?? null)
  const workspaceCurrency = (activeWorkspace?.settings?.currency ?? 'USD').toUpperCase()
  const cardBackgroundsEnabled = activeWorkspace?.cardBackgroundSettings?.enabled !== false
  const shouldUseTabbedPopup = isPopupPresentation && !isMobileViewport

  const effectiveBudgetLimit = liveChecklistDoc?.budgetLimit ?? node.budgetLimit
  const effectiveActualSpend = liveChecklistDoc?.actualSpend ?? node.actualSpend
  const effectiveEstimatedCost = liveChecklistDoc?.estimatedCost ?? node.estimatedCost

  useEffect(() => {
    if (isDebugMode || !budgetEnabled || !node.workspaceId) {
      setExpenses([])
      return
    }
    return subscribeEntityExpenses(node.workspaceId, node.id, setExpenses, (error) => {
      console.warn('[NodeDetailDrawer] budget expense subscription failed', error)
    })
  }, [budgetEnabled, isDebugMode, node.workspaceId, node.id])

  // Keep the editable draft of budgetLimit synced when the saved node changes.
  useEffect(() => {
    const limit = effectiveBudgetLimit
    setBudgetLimitDraft(typeof limit === 'number' ? (limit / 100).toFixed(2) : '')
  }, [effectiveBudgetLimit, node.id])

  // Tick so the lifecycle chip transitions Ready → Active → Over over time.
  useEffect(() => {
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 30_000)
    return () => window.clearInterval(intervalId)
  }, [])

  useEffect(() => () => {
    audioStreamRef.current?.getTracks().forEach((track) => track.stop())
    if (ratingToastTimerRef.current !== null) window.clearTimeout(ratingToastTimerRef.current)
  }, [])

  // ---- Edit lock & deferred draft ------------------------------------------

  // What the editable controls display: saved node overlaid with the live draft.
  const view = ({ ...node, ...draft } as BoardChecklistItem)
  const isRecurringOccurrenceScoped = Boolean(
    !isNewNode
    && focusedOccurrenceKey
    && (node.checklistType === 'DAILY' || node.checklistType === 'WEEKLY'),
  )
  const combinedDraft = useMemo(() => {
    const next: Partial<BoardChecklistItem> = { ...draft }
    const normalizedTitle = truncateToLimit(titleDraft, checklistLimits.titleMaxLength)
    if (normalizedTitle !== node.title) next.title = normalizedTitle
    return next
  }, [draft, checklistLimits.titleMaxLength, node.title, titleDraft])
  // Structural compare — nested arrays/objects (stockRequirements, targetCoordinates,
  // autoSpawnTemplates, blockedByExternal, …) always fail Object.is after any
  // setDraftField spread even if content is unchanged, which spuriously inflates
  // the dirty set and triggers the recurring scope dialog when nothing actually
  // changed. JSON compare is safe here: draft values are the same serializable
  // checklist fields Firestore round-trips.
  const changedDraftKeys = useMemo(
    () => Object.keys(combinedDraft).filter((key) => {
      const before = (node as unknown as Record<string, unknown>)[key]
      const after = (combinedDraft as Record<string, unknown>)[key]
      if (Object.is(before, after)) return false
      if (before === null || after === null) return true
      if (typeof before !== 'object' || typeof after !== 'object') return true
      try {
        return JSON.stringify(before) !== JSON.stringify(after)
      } catch {
        return true
      }
    }),
    [combinedDraft, node],
  )
  const hasRecurringGlobalDraft = isRecurringOccurrenceScoped
    && changedDraftKeys.some((key) => isChecklistRecurringGlobalField(key))
  const hasRecurringVersionedDraft = isRecurringOccurrenceScoped
    && changedDraftKeys.some((key) => isChecklistDetailVersionedField(key))

  const hasManualSaveDraft = useMemo(() => {
    return changedDraftKeys.some((key) => isManualSaveField(key))
  }, [changedDraftKeys])

  // Enable the header Save action only when there is something to persist:
  // brand-new drafts need a non-empty title; existing items need at least one
  // dirty manual-save field.
  const hasPendingChanges = isNewNode
    ? titleDraft.trim().length > 0
    : hasManualSaveDraft

  // Arm 20s idle release when no dirty manual-save draft exists.
  useEffect(() => {
    editLock.armIdleRelease(!hasManualSaveDraft)
  }, [hasManualSaveDraft, editLock])

  // Keep titleDraft in sync with node.title when there are no unsaved manual edits.
  useEffect(() => {
    if (!hasManualSaveDraft) {
      setTitleDraft(node.title)
    }
  }, [hasManualSaveDraft, node.title])

  const setDraftField = withLock((updates: Partial<BoardChecklistItem>) => {
    const meaningfulUpdates = Object.fromEntries(
      Object.entries(updates).filter(([key, value]) => !Object.is((view as unknown as Record<string, unknown>)[key], value)),
    ) as Partial<BoardChecklistItem>
    if (Object.keys(meaningfulUpdates).length === 0) return
    setDraft((current) => ({ ...current, ...meaningfulUpdates }))
  })

  const commitDraft = async (opts: { scope?: 'single' | 'future' | 'global' | null } = {}) => {
    if (isNewNode) {
      if (!onCreate) {
        throw new Error('Checklist creation is not connected here.')
      }
      // Brand-new draft: build the final node by overlaying the draft on the
      // placeholder we were handed and emit a single onCreate. The local title
      // input lives in `titleDraft` (not the draft object), so fold it in.
      const finalNode: BoardChecklistItem = {
        ...node,
        ...draft,
        title: truncateToLimit(titleDraft, checklistLimits.titleMaxLength),
      }
      if (onCreate) await onCreate(finalNode)
      return
    }
    if (onSaveDraft) {
      await onSaveDraft(node, combinedDraft, opts)
      return
    }
    const { weightage, title, ...rest } = draft
    if (Object.keys(rest).length > 0) await onVerificationChange(node, rest)
    if (typeof title === 'string') await onTitleBlur(node, title)
    if (weightage !== undefined) await onWeightageBlur(node, String(weightage))
  }

  const performSave = async (scope?: 'single' | 'future' | 'global' | null) => {
    await commitDraft({ scope })
    if (!isNewNode) await editLock.release()
    setDraft({})
  }

  // For brand-new drafts the title must be non-empty before Save is allowed.
  const newDraftReady = !isNewNode || titleDraft.trim().length > 0
  const saveDraftAndCloseIfNew = async (scope?: 'single' | 'future' | 'global' | null) => {
    if (!newDraftReady) {
      flashRatingToast('Add a title before saving.')
      return
    }
    if (isSavingDraft) return
    setIsSavingDraft(true)
    try {
      await performSave(scope)
      if (isNewNode) onClose()
    } catch (error) {
      flashRatingToast(error instanceof Error ? error.message : 'Could not save this checklist item.')
    } finally {
      setIsSavingDraft(false)
    }
  }

  const completeConfirmedSave = async (scope?: 'single' | 'future' | 'global' | null) => {
    if (isSavingDraft) return
    setIsSavingDraft(true)
    try {
      const shouldClose = saveAndCloseAfterConfirm
      await performSave(scope)
      setTitleScopeDialogOpen(false)
      setRecurringScopeDialogOpen(false)
      setSaveAndCloseAfterConfirm(false)
      if (shouldClose) onClose()
    } catch (error) {
      flashRatingToast(error instanceof Error ? error.message : 'Could not save this checklist item.')
    } finally {
      setIsSavingDraft(false)
    }
  }

  const applyPendingAssignee = withLock(async (scope: 'single' | 'future' | 'global') => {
    const pending = pendingRecurringAssignee
    if (!pending) return
    try {
      await onAssignmentChange(node, pending.assignee, {
        scope,
        pageKey: focusedOccurrenceKey ?? null,
      })
    } catch (error) {
      flashRatingToast(error instanceof Error ? error.message : 'Could not update the assignee.')
    } finally {
      setPendingRecurringAssignee(null)
    }
  })

  const startRecurringSaveFlow = (closeAfterSave: boolean) => {
    if (!isRecurringOccurrenceScoped) {
      void saveDraftAndCloseIfNew()
      return
    }
    setSaveAndCloseAfterConfirm(closeAfterSave)
    if (hasRecurringGlobalDraft) {
      setTitleScopeDialogOpen(true)
      return
    }
    if (hasRecurringVersionedDraft) {
      setRecurringScopeDialogOpen(true)
      return
    }
    void completeConfirmedSave()
  }

  const handleSaveClick = () => {
    startRecurringSaveFlow(false)
  }

  const handleCloseRequest = () => {
    if (isNewNode ? hasPendingChanges : hasManualSaveDraft) {
      setCloseDialogOpen(true)
      return
    }
    if (!isNewNode) void editLock.release()
    onClose()
  }

  // ---- Derived data --------------------------------------------------------

  const checklistProgressById = useMemo(() => {
    const map = new Map<string, number>()
    boardNodes.forEach((item) => map.set(item.id, item.isCompleted ? 100 : 0))
    return map
  }, [boardNodes])

  const blockedByLinks = useMemo(() => resolveBlockedByLinks(view, boardNodes), [boardNodes, view])
  const blockerForLinks = useMemo(() => resolveBlockerForLinks(view, boardNodes), [boardNodes, view])
  const savedBlockedByLinks = useMemo(() => resolveBlockedByLinks(node, boardNodes), [boardNodes, node])
  // Lifecycle chip + lock reflect the SAVED node (not the unsaved draft).
  const savedOpenBlockerTitles = getOpenBlockerTitles(savedBlockedByLinks, (id) => checklistProgressById.get(id))
  const isBlocked = savedOpenBlockerTitles.length > 0 || isLocked

  const lifecycleChip = getLifecycleChip(node, { isBlocked, openBlockerTitles: savedOpenBlockerTitles, nowMs })
  const workflowStatus = getWorkflowStatus(isNewNode ? view : node)

  const totalBoardWeight = useMemo(
    () => boardNodes.reduce((sum, boardNode) => sum + normalizeWeightage(boardNode.weightage), 0),
    [boardNodes],
  )
  const currentWeight = Math.min(checklistLimits.weightageMax, normalizeWeightage(view.weightage))
  const projectedBoardWeight = Math.max(1, totalBoardWeight)
  const impactLabel = `${((currentWeight / projectedBoardWeight) * 100).toFixed(1)}% impact`

  // Deferred-field controls are editable only when inputs are enabled.
  const inputsDisabled = !canEdit || isReadOnly || Boolean(editLock.holder && !editLock.heldByMe)

  // ---- Handlers ------------------------------------------------------------
  // (flashRatingToast is hoisted near the top so the edit-mode state machine
  // and the new-draft validator can use it without a forward reference.)

  const handleRatingSelect = (key: RatingDimensionKey, star: number) => {
    const field: keyof BoardChecklistItem =
      key === 'complexity' ? 'complexityStars' : key === 'urgency' ? 'urgencyStars' : 'importanceStars'
    setDraftField({ [field]: star } as Partial<BoardChecklistItem>)
    flashRatingToast(`${RATING_DIMENSIONS[key].label} marked as ${getRatingOptionLabel(key, star)}`)
  }

  const sendComment = withLock(async () => {
    const text = truncateToLimit(commentDraft.trim(), checklistLimits.commentMaxLength)
    if (isCommentSending || (!text && commentAttachmentFiles.length === 0) || !currentUser || !operationalPermissions.comment) return

    const mentionedUsers = workspaceMembers
      .filter((member) => member.userId !== currentUser.uid && text.toLocaleLowerCase().includes(`@${member.displayName}`.toLocaleLowerCase()))
      .map((member) => ({ userId: member.userId, displayName: member.displayName }))

    setIsCommentSending(true)
    try {
      if (editingCommentId) {
        const existing = comments.find((comment) => comment.id === editingCommentId)
        if (!existing || existing.userId !== currentUser.uid) return
        await updateDoc(doc(firestoreDb, 'checklist_comments', editingCommentId), {
          text,
          mentionedUserIds: mentionedUsers.map((member) => member.userId),
          mentionedUsers,
          editedAt: new Date().toISOString(),
        })
        await recordWorkspaceActivity(firestoreDb, {
          workspaceId: node.workspaceId,
          actorId: currentUser.uid,
          actorName: currentDisplayName || currentUser.email,
          targetType: 'CHECKLIST',
          targetId: node.id,
          targetTitle: node.title,
          action: 'COMMENT_EDIT',
          summary: 'Edited a checklist comment',
          board: node.board ?? 'DASHBOARD',
        })
        setEditingCommentId(null)
        setCommentDraft('')
        return
      }

      const commentRef = doc(collection(firestoreDb, 'checklist_comments'))
      const attachments = await uploadChecklistCommentAttachments({
        workspaceId: node.workspaceId,
        checklistItemId: node.id,
        commentId: commentRef.id,
        files: commentAttachmentFiles,
        identity: {
          userId: currentUser.uid,
          userName: currentDisplayName || currentUser.email || 'Teammate',
        },
      })
      const comment: ChecklistComment & {
        checklistItemId: string
        parentContainerId: string
        workspaceId: string
        pageKey?: string | null
      } = {
        id: commentRef.id,
        checklistItemId: node.id,
        parentContainerId: node.parentContainerId,
        workspaceId: node.workspaceId,
        ...(focusedOccurrenceKey ? { pageKey: focusedOccurrenceKey } : {}),
        userId: currentUser.uid,
        userName: currentDisplayName || currentUser.email || 'Teammate',
        text,
        timestamp: new Date().toISOString(),
        parentCommentId: replyToCommentId,
        mentionedUserIds: mentionedUsers.map((member) => member.userId),
        mentionedUsers,
        reactions: {},
        editedAt: null,
        deletedAt: null,
        attachments,
        attachmentCount: attachments.length,
        ...(currentProfileImageUrl ? { userAvatarUrl: currentProfileImageUrl } : {}),
      }

      await setDoc(commentRef, comment)
      // Firestore atomic increment — was `comments.length + 1` before, which
      // was a client-derived count that raced concurrent commenters (two
      // users writing simultaneously both see N and both write N+1, losing
      // one increment). `increment(1)` is server-computed and lock-free.
      await writeChecklistUpdate(
        node.id,
        node.checklistType === 'DAILY' || node.checklistType === 'WEEKLY'
          ? { commentsCountDelta: 1 }
          : { commentsCount: increment(1) },
        { uid: currentUser.uid, displayName: currentDisplayName || currentUser.email || 'Teammate' },
        { pageKey: focusedOccurrenceKey ?? null },
      )
      await recordWorkspaceActivity(firestoreDb, {
        workspaceId: node.workspaceId,
        actorId: currentUser.uid,
        actorName: currentDisplayName || currentUser.email,
        targetType: 'CHECKLIST',
        targetId: node.id,
        targetTitle: node.title,
        action: 'COMMENT_CREATE',
        summary: replyToCommentId ? 'Replied to a checklist comment' : 'Added a checklist comment',
        board: node.board ?? 'DASHBOARD',
        metadata: { mentionedUserIds: mentionedUsers.map((member) => member.userId) },
      })
      setCommentDraft('')
      setCommentAttachmentFiles([])
      setReplyToCommentId(null)
      setMentionQuery(null)
    } finally {
      setIsCommentSending(false)
    }
  })

  const softDeleteComment = async (comment: ChecklistComment) => {
    if (!currentUser || !operationalPermissions.comment || (comment.userId !== currentUser.uid && !roleCanPerform(activeRole, 'ADMIN'))) return
    await updateDoc(doc(firestoreDb, 'checklist_comments', comment.id), {
      text: '',
      deletedAt: new Date().toISOString(),
      deletedBy: currentUser.uid,
      mentionedUserIds: [],
      mentionedUsers: [],
    })
    await writeChecklistUpdate(
      node.id,
      node.checklistType === 'DAILY' || node.checklistType === 'WEEKLY'
        ? { commentsCountDelta: -1 }
        : { commentsCount: increment(-1) },
      { uid: currentUser.uid, displayName: currentDisplayName || currentUser.email || 'Teammate' },
      { pageKey: focusedOccurrenceKey ?? null },
    )
    await recordWorkspaceActivity(firestoreDb, {
      workspaceId: node.workspaceId,
      actorId: currentUser.uid,
      actorName: currentDisplayName || currentUser.email,
      targetType: 'CHECKLIST',
      targetId: node.id,
      targetTitle: node.title,
      action: 'COMMENT_DELETE',
      summary: 'Deleted a checklist comment',
      board: node.board ?? 'DASHBOARD',
    })
  }

  const toggleReaction = async (comment: ChecklistComment, emoji: string) => {
    if (!currentUser || comment.deletedAt || !operationalPermissions.comment) return
    const users = comment.reactions?.[emoji] ?? []
    await updateDoc(doc(firestoreDb, 'checklist_comments', comment.id), {
      [`reactions.${emoji}`]: users.includes(currentUser.uid) ? arrayRemove(currentUser.uid) : arrayUnion(currentUser.uid),
    })
    await recordWorkspaceActivity(firestoreDb, {
      workspaceId: node.workspaceId,
      actorId: currentUser.uid,
      actorName: currentDisplayName || currentUser.email,
      targetType: 'CHECKLIST',
      targetId: node.id,
      targetTitle: node.title,
      action: 'COMMENT_REACTION',
      summary: `${users.includes(currentUser.uid) ? 'Removed' : 'Added'} ${emoji} reaction`,
      board: node.board ?? 'DASHBOARD',
    })
  }

  const handleCommentDraftChange = (value: string) => {
    const next = truncateToLimit(value, checklistLimits.commentMaxLength)
    setCommentDraft(next)
    const match = next.match(/(?:^|\s)@([^\s@]*)$/)
    setMentionQuery(match ? match[1] : null)
  }

  const insertMention = (member: ChecklistMemberOption) => {
    setCommentDraft((current) => current.replace(/(?:^|\s)@[^\s@]*$/, (token) => `${token.startsWith(' ') ? ' ' : ''}@${member.displayName} `))
    setMentionQuery(null)
  }

  const scrollToSection = useCallback((section: BlockerCtaSection) => {
    if (shouldUseTabbedPopup) {
      if (section === 'proof' || section === 'location' || section === 'approver') {
        setActiveTabId('proofAndAttachments')
      } else if (section === 'dependencies') {
        setActiveTabId('dependencies')
      } else if (section === 'resources') {
        setActiveTabId('financials')
      } else if (section === 'assignee') {
        setActiveTabId('details')
      }
    }
    setTimeout(() => {
      const el = document.getElementById('drawer-section-' + section)
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      el.setAttribute('data-drawer-highlight', 'true')
      setTimeout(() => {
        el.removeAttribute('data-drawer-highlight')
      }, 1500)
    }, shouldUseTabbedPopup ? 50 : 0)
  }, [shouldUseTabbedPopup])

  const handleWorkflowStatusChange = withLock((status: ChecklistWorkflowStatus) => {
    if (isNewNode) {
      setPermissionNotice(null)
      setDraftField({
        workflowStatus: status,
        isCompleted: status === 'COMPLETED',
        ...(status === 'COMPLETED' ? { previousWorkflowStatus: getWorkflowStatus(view) } : { previousWorkflowStatus: null }),
      } as Partial<BoardChecklistItem>)
      return
    }
    // Permission check first — surfaces disabled reason via toast if somehow bypassed
    if (!operationalPermissions.status) return
    if (
      status === 'NOT_REQUIRED'
      && (node.checklistType === 'DAILY' || node.checklistType === 'WEEKLY')
    ) {
      const evalCheck = canPickInDropdown(
        view,
        currentUser ? { uid: currentUser.uid, role: activeRole } : null,
        'NOT_REQUIRED',
      )
      if (!evalCheck.ok) {
        void onStatusChange(node, status)
        return
      }
      setNotNeededScopeChoice('THIS_PERIOD')
      setNotNeededScopeDialogOpen(true)
      return
    }
    const clearScope = status !== 'NOT_REQUIRED' && node.notRequiredFrom ? { notRequiredScope: 'CLEAR' as const } : undefined
    // Delegates to controller.updateStatus which does the blocker check + banner routing
    if (clearScope) {
      void onStatusChange(node, status, clearScope)
    } else {
      void onStatusChange(node, status)
    }
  })

  const stopAudioRecording = () => {
    if (audioRecorderRef.current?.state === 'recording') audioRecorderRef.current.stop()
  }

  const startAudioRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      audioStreamRef.current = stream
      audioChunksRef.current = []
      const recorder = new MediaRecorder(stream)
      audioRecorderRef.current = recorder
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data)
      }
      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
        if (audioBlob.size > 0) {
          void persistMemo(audioBlob, `voice-memo-${Date.now()}.webm`)
        }
        audioStreamRef.current?.getTracks().forEach((track) => track.stop())
        audioStreamRef.current = null
        audioRecorderRef.current = null
        setIsAudioRecording(false)
      }
      recorder.start()
      setIsAudioRecording(true)
      window.setTimeout(() => {
        if (audioRecorderRef.current?.state === 'recording') audioRecorderRef.current.stop()
      }, 30_000)
    } catch {
      setLocationNotice('Microphone access failed. Allow microphone permission and retry.')
    }
  }

  const uploadIdentity = currentUser ? {
    userId: currentUser.uid,
    userName: currentDisplayName || currentUser.email || 'Teammate',
  } : null

  const persistLocalFile = withLock(async (file: File) => {
    if (!uploadIdentity || !operationalPermissions.proof) return
    const attachment = await uploadChecklistProof({
      workspaceId: node.workspaceId,
      checklistItemId: node.id,
      pageKey: focusedOccurrenceKey ?? null,
      file,
      fileName: file.name,
      kind: 'PROOF',
      identity: uploadIdentity,
    })
    const isRepeating = node.checklistType === 'DAILY' || node.checklistType === 'WEEKLY'
    const { fieldUpdates, enteredAssessing } = buildProofSubmissionEffects({ item: node, attachment })
    if (isRepeating) {
      await submitRepeatingProofEffects({
        node,
        focusedOccurrenceKey: focusedOccurrenceKey ?? null,
        fieldUpdates,
        enteredAssessing,
        actor: { id: currentUser?.uid, name: currentDisplayName || currentUser?.email },
      })
    } else {
      await onVerificationChange(node, fieldUpdates)
    }
  })

  const handleProofFiles = (files: FileList) => {
    const file = files[0]
    if (file) void persistLocalFile(file)
  }

  const handleProofDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()
    if (isReadOnly || !operationalPermissions.proof) return
    const file = event.dataTransfer.files[0]
    if (file) void persistLocalFile(file)
  }

  const persistCardBackground = withLock(async (file: File) => {
    if (!canEdit || isReadOnly || !cardBackgroundsEnabled) return
    setCardBackgroundBusy(true)
    try {
      const updates = await uploadChecklistCardBackground({
        workspaceId: node.workspaceId,
        checklistItemId: node.id,
        file,
      })
      await onVerificationChange(node, updates)
      flashRatingToast('Card background updated.')
    } catch (error) {
      setPermissionNotice(error instanceof Error ? error.message : 'Could not update card background.')
    } finally {
      setCardBackgroundBusy(false)
    }
  })

  const clearCardBackground = withLock(async () => {
    if (!canEdit || isReadOnly || !cardBackgroundsEnabled) return
    setCardBackgroundBusy(true)
    try {
      await onVerificationChange(node, clearChecklistCardBackgroundUpdates())
      flashRatingToast('Card background removed.')
    } catch (error) {
      setPermissionNotice(error instanceof Error ? error.message : 'Could not remove card background.')
    } finally {
      setCardBackgroundBusy(false)
    }
  })

  const persistMemo = withLock(async (blob: Blob, fileName: string) => {
    if (!uploadIdentity || !operationalPermissions.memo) return
    const attachment = await uploadChecklistProof({
      workspaceId: node.workspaceId,
      checklistItemId: node.id,
      pageKey: focusedOccurrenceKey ?? null,
      file: blob,
      fileName,
      kind: 'MEMO',
      identity: uploadIdentity,
    })
    const isVideo = attachment.contentType.startsWith('video/')
    await onVerificationChange(node, {
      proofVideoUrl: isVideo ? attachment.downloadUrl : null,
      voiceMemoBlobUrl: isVideo ? null : attachment.downloadUrl,
      memoStoragePath: attachment.storagePath,
      memoFileName: attachment.name,
      memoContentType: attachment.contentType,
      memoUploadedById: attachment.uploadedById,
      memoUploadedByName: attachment.uploadedByName,
      memoUploadedAt: attachment.uploadedAt,
    })
  })

  const captureTargetLocation = withLock(() => {
    if (!navigator.geolocation) {
      setLocationNotice('Location capture is not available in this browser.')
      return
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        void onVerificationChange(node, {
          targetCoordinates: { lat: position.coords.latitude, lng: position.coords.longitude },
        })
        setLocationNotice('Target location captured.')
      },
      () => setLocationNotice('Location capture failed. Allow browser location access and retry.'),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 12_000 },
    )
  })

  const closeRecorder = () => {
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop()
    mediaRecorderRef.current = null
    recordingStream?.getTracks().forEach((track) => track.stop())
    setRecordingStream(null)
    setIsRecording(false)
    setIsVideoRecorderOpen(false)
  }

  const openRecorder = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      setRecordingStream(stream)
      setIsVideoRecorderOpen(true)
      window.setTimeout(() => {
        if (videoPreviewRef.current) videoPreviewRef.current.srcObject = stream
      }, 0)
    } catch {
      setLocationNotice('Camera access failed. Allow camera permission and retry.')
    }
  }

  const startRecording = () => {
    if (!recordingStream) return
    recordedChunksRef.current = []
    const recorder = new MediaRecorder(recordingStream)
    mediaRecorderRef.current = recorder
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) recordedChunksRef.current.push(event.data)
    }
    recorder.onstop = () => {
      const videoBlob = new Blob(recordedChunksRef.current, { type: 'video/webm' })
      if (videoBlob.size > 0) {
        void persistMemo(videoBlob, `video-memo-${Date.now()}.webm`)
      }
      recordingStream.getTracks().forEach((track) => track.stop())
      setRecordingStream(null)
      setIsRecording(false)
      setIsVideoRecorderOpen(false)
    }
    recorder.start()
    setIsRecording(true)
    window.setTimeout(() => {
      if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop()
    }, 15_000)
  }

  // ---- Section renderers ---------------------------------------------------

  // ---- Financials handlers -------------------------------------------------
  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false)
  const expenseFormSections = useMemo(() => getBudgetExpenseFormSections(workspaceCurrency), [workspaceCurrency])

  const handleSaveExpenseForm = withLock(async (values: Record<string, unknown>) => {
    if (!currentUser || !canMutateFinancials || financialsBusy) return
    const minor = Math.round(Number(values.amount ?? 0))
    const description = String(values.description ?? '').trim()
    const category = (values.category as BudgetCategory) || 'Other'
    const isRecurring = Boolean(values.isRecurring)
    const cadence = (values.recurringCadence as 'weekly' | 'monthly' | 'quarterly' | 'yearly') || 'monthly'
    const dayOfPeriod = Number(values.recurringDayOfPeriod ?? 1)

    setFinancialsBusy(true)
    setFinancialsNotice(null)
    try {
      let recurringId: string | null = null
      const taxAmount = values.taxAmount != null && values.taxAmount !== '' ? Math.round(Number(values.taxAmount)) : null
      const taxInclusive = values.taxInclusive !== false
      const vendorId = values.vendorId ? String(values.vendorId) : null
      const tags = Array.isArray(values.tags) ? (values.tags as string[]) : []
      const paymentMethod = (values.paymentMethod as 'cash' | 'card' | 'bank' | 'upi' | 'other') || 'card'
      const isIncome = Boolean(values.isIncome)
      const entityChain = buildBudgetEntityChain({
        id: node.id,
        type: 'CHECKLIST',
        title: node.title,
        parentContainerId: node.parentContainerId ?? (node as any).parentId,
      }, candidateElements)
      const entityTitles = entityChain.map((e) => e.title || '')

      const template = {
        amount: minor,
        currency: workspaceCurrency,
        description,
        category,
        entityChain,
        entityTitles,
        vendorId,
        tags,
        taxAmount,
        taxInclusive,
        paymentMethod,
        isIncome,
      }

      if (isRecurring) {
        recurringId = await createBudgetRecurringRule({
          workspaceId: node.workspaceId,
          template,
          cadence,
          dayOfPeriod,
          actor: { actorId: currentUser.uid, actorName: currentDisplayName || currentUser.email || null },
        })
      }

      let receipt: { storagePath?: string | null } | null = null
      if (typeof values.receipt === 'string' && values.receipt) {
        receipt = { storagePath: values.receipt }
      }

      const result = await logExpense({
        workspaceId: node.workspaceId,
        entityChain,
        amount: minor,
        currency: workspaceCurrency,
        description,
        category,
        receipt,
        taxAmount,
        taxInclusive,
        vendorId,
        tags,
        paymentMethod,
        recurringId,
        isIncome,
        audit: {
          actorId: currentUser.uid,
          actorName: currentDisplayName || currentUser.email || null,
        },
        entityTitles: entityChain.map((item) => item.title || node.title),
      })

      setIsExpenseDialogOpen(false)
      const categoryMsg = result?.overCategoryLimit
        ? t('NLSBudgetExpenseOverCategoryLimit', { category, period: result.overCategoryLimit })
        : null
      if (categoryMsg) {
        showToast(categoryMsg, 'warning')
      }
      if (result?.exceeded) {
        showToast('Expense logged — this entry pushed the node over budget.', 'warning')
      }
      if (result?.exceeded && categoryMsg) {
        setFinancialsNotice(`Expense logged — this entry pushed the node over budget. ${categoryMsg}`)
      } else if (result?.exceeded) {
        setFinancialsNotice('Expense logged — this entry pushed the node over budget.')
      } else if (categoryMsg) {
        setFinancialsNotice(categoryMsg)
      } else {
        setFinancialsNotice('Expense logged.')
      }
    } catch (error) {

      if (error instanceof BudgetModuleDisabledError) {
        setFinancialsNotice('Budget module is disabled for this workspace.')
      } else if (error instanceof BudgetCurrencyMismatchError) {
        setFinancialsNotice(`Currency mismatch — workspace uses ${error.expected}.`)
      } else {
        console.warn('[NodeDetailDrawer] logExpense failed', error)
        setFinancialsNotice('Could not log expense.')
      }
    } finally {
      setFinancialsBusy(false)
    }
  })

  const handleReverseExpense = withLock(async (entry: WorkspaceExpense) => {
    if (!currentUser || !canMutateFinancials || financialsBusy) return
    if (entry.reversesExpenseId) return // reversal entries cannot be reversed again
    setFinancialsBusy(true)
    setFinancialsNotice(null)
    try {
      const entityChain = buildBudgetEntityChain({
        id: node.id,
        type: 'CHECKLIST',
        title: node.title,
        parentContainerId: node.parentContainerId ?? (node as any).parentId,
      }, candidateElements)

      await reverseExpense({
        workspaceId: node.workspaceId,
        originalExpenseId: entry.id,
        entityChain,
        description: `Reversal of "${entry.description}"`,
        audit: {
          actorId: currentUser.uid,
          actorName: currentDisplayName || currentUser.email || null,
        },
      })
      setFinancialsNotice('Reversal recorded.')
    } catch (error) {
      console.warn('[NodeDetailDrawer] reverseExpense failed', error)
      setFinancialsNotice('Could not reverse expense.')
    } finally {
      setFinancialsBusy(false)
    }
  })

  const handleBudgetLimitBlur = withLock(async () => {
    if (!currentUser || !canMutateFinancials) return
    const trimmed = budgetLimitDraft.trim()
    const next = trimmed.length === 0 ? null : Math.round(Number(trimmed.replace(/,/g, '.')) * 100)
    if (next !== null && (!Number.isFinite(next) || next < 0)) {
      setFinancialsNotice('Budget must be a non-negative number.')
      return
    }
    if (next === (effectiveBudgetLimit ?? null)) return
    try {
      await setBudgetLimitForEntity({
        workspaceId: node.workspaceId,
        entity: { type: 'CHECKLIST', id: node.id, docPath: `checklists/${node.id}` },
        budgetLimit: next,
        audit: {
          actorId: currentUser.uid,
          actorName: currentDisplayName || currentUser.email || null,
        },
        entityTitle: node.title,
      })
    } catch (error) {
      console.warn('[NodeDetailDrawer] setBudgetLimit failed', error)
      setFinancialsNotice('Could not update budget limit.')
    }
  })

  const handleEstimatedCostBlur = withLock(async (rawValue: string) => {
    if (!currentUser || !canMutateFinancials) return
    const trimmed = rawValue.trim()
    const next = trimmed.length === 0 ? null : Math.round(Number(trimmed.replace(/,/g, '.')) * 100)
    if (next !== null && (!Number.isFinite(next) || next < 0)) return
    if (next === (effectiveEstimatedCost ?? null)) return
    try {
      await writeChecklistUpdate(
        node.id,
        { estimatedCost: next },
        { uid: currentUser?.uid ?? '', displayName: currentDisplayName || currentUser?.email || null },
      )
    } catch (error) {
      console.warn('[NodeDetailDrawer] estimatedCost update failed', error)
    }
  })

  const actualSpend = typeof effectiveActualSpend === 'number' ? effectiveActualSpend : 0
  const budgetLimit = typeof effectiveBudgetLimit === 'number' ? effectiveBudgetLimit : null
  const rawEstimatedCost = effectiveEstimatedCost
  const estimatedCost = typeof rawEstimatedCost === 'number' ? rawEstimatedCost : null
  const currentBudgetBinding = liveChecklistDoc?.bindings?.budgetBinding ?? node.bindings?.budgetBinding
  const isRecordEstimateEnabled = Boolean(currentBudgetBinding)
  const hasEstimate = typeof estimatedCost === 'number' && estimatedCost > 0

  const [bindingCategory, setBindingCategory] = useState<BudgetCategory>(() => {
    if (currentBudgetBinding?.category && (BUDGET_CATEGORIES as readonly string[]).includes(currentBudgetBinding.category)) {
      return currentBudgetBinding.category as BudgetCategory
    }
    try {
      const saved = window.localStorage.getItem('timelining:last-budget-category')
      return (BUDGET_CATEGORIES as readonly string[]).includes(saved ?? '') ? saved as BudgetCategory : DEFAULT_BUDGET_CATEGORY
    } catch {
      return DEFAULT_BUDGET_CATEGORY
    }
  })

  useEffect(() => {
    if (currentBudgetBinding?.category && (BUDGET_CATEGORIES as readonly string[]).includes(currentBudgetBinding.category)) {
      setBindingCategory(currentBudgetBinding.category as BudgetCategory)
    }
  }, [currentBudgetBinding?.category])

  const handleToggleBudgetBinding = withLock(async (enabled: boolean) => {
    if (!currentUser || !canMutateFinancials || !hasEstimate) return
    try {
      const actor = { uid: currentUser.uid, displayName: currentDisplayName || currentUser.email || null }
      if (enabled) {
        const cat = bindingCategory
        await writeChecklistUpdate(
          node.id,
          {
            bindings: {
              budgetBinding: { category: cat, spendTiming: 'ON_COMPLETION' },
            },
          },
          actor,
        )
      } else {
        await writeChecklistUpdate(
          node.id,
          {
            bindings: {
              budgetBinding: deleteField(),
            },
          },
          actor,
        )
      }
    } catch (error) {
      console.warn('[NodeDetailDrawer] budgetBinding update failed', error)
      setFinancialsNotice(t('NLSChecklistFieldBudgetBindingUpdateFailed'))
    }
  })

  const handleBudgetBindingCategoryChange = withLock(async (newCategory: BudgetCategory) => {
    setBindingCategory(newCategory)
    try {
      window.localStorage.setItem('timelining:last-budget-category', newCategory)
    } catch {
      // ignore
    }
    if (!currentUser || !canMutateFinancials || !isRecordEstimateEnabled) return
    try {
      const actor = { uid: currentUser.uid, displayName: currentDisplayName || currentUser.email || null }
      await writeChecklistUpdate(
        node.id,
        {
          bindings: {
            budgetBinding: { category: newCategory, spendTiming: 'ON_COMPLETION' },
          },
        },
        actor,
      )
    } catch (error) {
      console.warn('[NodeDetailDrawer] budgetBinding category update failed', error)
    }
  })

  const sideEffects = liveChecklistDoc?.sideEffects ?? node.sideEffects
  const budgetSpends = Array.isArray(sideEffects?.budgetSpends) ? sideEffects.budgetSpends : []
  const latestSpend = budgetSpends.length > 0 ? budgetSpends[budgetSpends.length - 1] : null
  const financialsBand = computeBudgetBand(actualSpend, budgetLimit)
  const formatMinor = (value: number | null) =>
    value === null ? '—' : `${workspaceCurrency} ${(value / 100).toFixed(2)}`
  const financialsChipText = formatMinor(actualSpend)
  const formatExpenseTime = (entry: WorkspaceExpense) => {
    const toDate = entry.timestamp && typeof entry.timestamp.toDate === 'function'
      ? entry.timestamp.toDate()
      : null
    if (!toDate) return 'Just now'
    const diffMs = Math.max(0, nowMs - toDate.getTime())
    const minutes = Math.floor(diffMs / 60_000)
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }
  const expenseCategoryClass = (category: BudgetCategory) =>
    `isCategory-${category.toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-')}`

  const financialsTone: StatusPillTone =
    financialsBand === 'green' ? 'success' : financialsBand === 'amber' ? 'warning' : 'danger'
  const financialsChip = (
    <StatusChip
      tone={financialsTone}
      className={`nodeStatusChip isState-${financialsBand === 'green' ? 'ready' : financialsBand === 'amber' ? 'active' : 'over'}`}
    >
      {financialsChipText}
      {budgetLimit !== null ? ` / ${formatMinor(budgetLimit)}` : ''}
    </StatusChip>
  )

  const renderFinancialsBody = () => {
    if (!budgetEnabled) return null
    const referenceValue =
      progressReference === 'budgetLimit' ? budgetLimit
        : progressReference === 'estimatedCost' ? estimatedCost
        : actualSpend
    const denominator = referenceValue && referenceValue > 0 ? referenceValue : null
    const ratio = denominator ? Math.min(2, actualSpend / denominator) : 0
    const fillPercent = denominator ? Math.min(100, ratio * 100) : 0
    const overflowPercent = denominator && ratio > 1 ? Math.min(100, (ratio - 1) * 100) : 0
    const progressLabel = denominator ? `${Math.round(Math.min(100, ratio * 100))}%` : '0%'
    const orphanedPaths = expenses.some((entry) => entry.linkedEntityPath.length === 0)
    const resourceSignals = buildResourceSignals({ workspace: activeWorkspace, node, completionStage: workflowStatus === 'COMPLETED' })
    const impactPreview = buildResourceImpactPreview(node, expenseCategory)
    const visibleExpenses = showAllExpenses ? expenses : expenses.slice(0, 5)
    const hiddenExpenseCount = Math.max(0, expenses.length - 5)

    return (
      <>
        <div className="nodeFinancialsStatHeader">
          <div className="nodeFinancialsReadouts">
            <div>
              <span>actual spend</span>
              <strong>{formatMinor(actualSpend)}</strong>
            </div>
            <div>
              <span>{progressReference === 'budgetLimit' ? 'budget' : progressReference === 'estimatedCost' ? 'estimate' : 'self'}</span>
              <strong>{formatMinor(referenceValue)}</strong>
            </div>
          </div>
          <div className="nodeFinancialsProgress" aria-label="Budget progress">
            <div className={`nodeFinancialsProgressTrack isBand-${financialsBand}`}>
              <div className="nodeFinancialsProgressFill" style={{ width: `${fillPercent}%` }} />
              {overflowPercent > 0 && (
                <div className="nodeFinancialsProgressOverflow" style={{ width: `${overflowPercent}%` }} />
              )}
            </div>
            <span>{progressLabel}</span>
          </div>
          <div id="drawer-section-resources" className="nodeFinancialsImpactRow">
            <ResourceBadges signals={resourceSignals.signals} />
            <StatusChip tone="info" className="nodeStatusChip">
              {impactPreview.quotaImpactLabel}: +{impactPreview.quotaContribution}
            </StatusChip>
            {impactPreview.stockRequirements.length > 0 && (
              <StatusChip tone="info" className="nodeStatusChip">
                {impactPreview.stockRequirements.length} stock requirement{impactPreview.stockRequirements.length === 1 ? '' : 's'}
              </StatusChip>
            )}
          </div>
        </div>

        {orphanedPaths && (
          <p className="nodeFinancialsBanner" role="status">
            Some expense entries reference a deleted parent. The roll-up is anchored to the snapshot at log time.
          </p>
        )}

        <div className="nodeFinancialsConfigCard">
          <span className="nodeSubHeader">Progress against</span>
          <div className="nodeFinancialsReferenceToggle" role="radiogroup" aria-label="Progress reference">
            {(['budgetLimit', 'estimatedCost', 'actualSpend'] as const).map((mode) => (
              <span key={mode} className={`nodeScheduleRadio ${progressReference === mode ? 'isActive' : ''}`}>
                <Radio
                  name={`progress-ref-${node.id}`}
                  checked={progressReference === mode}
                  label={mode === 'budgetLimit' ? 'Budget' : mode === 'estimatedCost' ? 'Estimate' : 'Self'}
                  onChange={() => setProgressReference(mode)}
                />
              </span>
            ))}
          </div>

          <div className="nodeFinancialsLimits">
            <div className="nodeDetailField nodeFinancialsMoneyField">
              <span>Budget limit</span>
              <div>
                <strong>{workspaceCurrency}</strong>
                <TextInput
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min={0}
                  value={budgetLimitDraft}
                  disabled={!canMutateFinancials || financialsBusy}
                  placeholder="0.00"
                  aria-label="Budget limit"
                  onChange={(val) => setBudgetLimitDraft(val)}
                  onBlur={() => void handleBudgetLimitBlur()}
                />
              </div>
            </div>
            <div className="nodeDetailField nodeFinancialsMoneyField">
              <span>Estimated cost</span>
              <div>
                <strong>{workspaceCurrency}</strong>
                <TextInput
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min={0}
                  defaultValue={estimatedCost !== null ? (estimatedCost / 100).toFixed(2) : ''}
                  disabled={!canMutateFinancials}
                  placeholder="0.00"
                  aria-label="Estimated cost"
                  onBlur={(event) => void handleEstimatedCostBlur(event.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="nodeBudgetBindingSection">
            <Toggle
              wrapperClassName={`nodeVerificationToggle ${isRecordEstimateEnabled ? 'isActive' : ''} ${!canMutateFinancials || !hasEstimate ? 'isDisabled' : ''}`}
              label={
                <>
                  <span className="nodeVerificationToggleIcon" aria-hidden="true">
                    <ReceiptLongOutlinedIcon fontSize="inherit" />
                  </span>
                  <span className="nodeVerificationToggleBody">
                    <strong>{t('NLSChecklistFieldRecordEstimateToggle')}</strong>
                    <small>
                      {!hasEstimate
                        ? t('NLSChecklistFieldRecordEstimateHintNoEstimate')
                        : isRecordEstimateEnabled
                          ? `${t('NLSChecklistFieldRecordEstimateCategoryLabel')}: ${bindingCategory}`
                          : t('NLSChecklistFieldRecordEstimateHintDisabled')}
                    </small>
                  </span>
                </>
              }
              checked={isRecordEstimateEnabled}
              disabled={!canMutateFinancials || !hasEstimate}
              onChange={(checked) => void handleToggleBudgetBinding(Boolean(checked))}
            />

            {isRecordEstimateEnabled && (
              <div className="nodeBudgetBindingCategoryPicker">
                <div className="nodeDetailField">
                  <span>{t('NLSChecklistFieldRecordEstimateCategoryLabel')}</span>
                  <Select
                    trigger="pill"
                    ariaLabel={t('NLSChecklistFieldRecordEstimateCategoryLabel')}
                    value={bindingCategory}
                    disabled={!canMutateFinancials}
                    options={BUDGET_CATEGORIES.map((cat) => ({ value: cat, label: cat }))}
                    onChange={(newCat) => void handleBudgetBindingCategoryChange(newCat as BudgetCategory)}
                  />
                </div>
              </div>
            )}

            {latestSpend && (
              <div className="nodeBudgetRecordedBanner" role="status">
                <ReceiptLongOutlinedIcon fontSize="small" />
                <span>
                  {t('NLSChecklistFieldRecordEstimateRecorded', {
                    amount: formatMinor(latestSpend.amountMinor),
                    date: new Date(latestSpend.appliedAt).toLocaleDateString(),
                  })}
                </span>
                <a
                  href={`/console/workspace/${activeWorkspace?.slug ?? activeWorkspace?.id ?? node.workspaceId}/budget?tab=LEDGER`}
                  className="nodeBudgetLedgerLink"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('NLSChecklistFieldRecordEstimateViewLedger')} →
                </a>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <Button
            variant="primary"
            disabled={!canMutateFinancials || financialsBusy}
            onClick={() => setIsExpenseDialogOpen(true)}
          >
            <AddRoundedIcon fontSize="small" />
            <span>+ Log expense</span>
          </Button>
        </div>
        {financialsNotice && <p className="nodeFinancialsNotice">{financialsNotice}</p>}

        <EntityFormDialog
          open={isExpenseDialogOpen}
          entityKind="expense"
          targetId="new"
          entityName="expense"
          entityTitle="Log Expense"
          sections={expenseFormSections}
          workspaceId={node.workspaceId}
          currency={workspaceCurrency}
          onClose={() => setIsExpenseDialogOpen(false)}
          onSave={handleSaveExpenseForm}
        />

        <div className="nodeFinancialsLedgerPreview" aria-label="Recent expense entries">
          {expenses.length === 0 ? (
            <p className="nodeDetailEmptyLine">
              <ReceiptLongOutlinedIcon fontSize="inherit" />
              No expenses recorded yet.
            </p>
          ) : (
            visibleExpenses.map((entry) => (
              <article key={entry.id} className={`nodeFinancialsLedgerRow nodeFinancialsExpenseRow ${entry.reversesExpenseId ? 'isReversal' : ''}`}>
                <span className={`nodeExpenseCategoryDot ${expenseCategoryClass(entry.category)}`} aria-hidden="true" />
                <div>
                  <span>{entry.description || (entry.reversesExpenseId ? 'Reversal' : 'Expense')}</span>
                  <small>{entry.category} · {formatExpenseTime(entry)}</small>
                  {entry.attachments && entry.attachments.length > 0 && (
                    <div className="expenseAttachmentChips">
                      {entry.attachments.map((attId) => (
                        <span key={attId} className="expenseAttachmentChip">
                          <span>{attId}</span>
                          <IconButton
                            variant="tertiary"
                            size="sm"
                            className="expenseAttachmentChipDetach"
                            label={`Detach attachment ${attId}`}
                            icon="×"
                            onClick={async (e) => {
                              e.stopPropagation()
                              await detachProvisionFromItem({
                                id: node.id,
                                workspaceId: node.workspaceId,
                                title: node.title,
                                kind: 'CHECKLIST',
                                collectionName: 'checklists',
                              }, 'upload', attId)
                              await updateDoc(doc(firestoreDb, WORKSPACE_EXPENSES_COLLECTION, entry.id), {
                                attachments: entry.attachments?.filter((id) => id !== attId) ?? [],
                              })
                              showToast('Attachment detached.', 'success')
                            }}
                          />
                          <IconButton
                            variant="tertiary"
                            size="sm"
                            tone="danger"
                            className="expenseAttachmentChipMore"
                            label={`Delete attachment ${attId} permanently`}
                            icon="⋮"
                            onClick={(e) => {
                              e.stopPropagation()
                              setPendingDeleteAttachment({ entry, attId })
                            }}
                          />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <strong>{formatMinor(entry.amount)}</strong>
                {!entry.reversesExpenseId && canMutateFinancials && (
                  <IconButton
                    variant="tertiary"
                    size="sm"
                    disabled={financialsBusy}
                    label={`Reverse ${entry.description || 'expense'}`}
                    icon={<UndoRoundedIcon fontSize="inherit" />}
                    onClick={() => void handleReverseExpense(entry)}
                  />
                )}
              </article>
            ))
          )}
        </div>
        {hiddenExpenseCount > 0 && (
          <Button
            variant="link"
            size="sm"
            className="nodeFinancialsMoreButton"
            onClick={() => setShowAllExpenses((current) => !current)}
          >
            {showAllExpenses ? 'Show fewer' : `Show ${hiddenExpenseCount} more`}
          </Button>
        )}
        <p className="nodeFinancialsLedgerFooter">
          Full history lives in the Audit Ledger. This view is summary-only.
        </p>
      </>
    )
  }

  // Risk fields are now rendered inline inside the combined "Details"
  // section (see below); the standalone Risk collapsible was removed as
  // part of the Basics+Risk merge.
  void lifecycleChip

  const dependenciesChip = (
    <StatusChip
      tone={isBlocked ? 'danger' : 'success'}
      className={`nodeStatusChip ${isBlocked ? 'isState-blocked' : 'isState-ready'}`}
    >
      {isBlocked ? t('NLSChecklistStatusBlocked') : t('NLSChecklistStatusClear')}
    </StatusChip>
  )

  const renderExternalLinksBlock = () => {
    if (isNewNode || !activeWorkspace || isPersonalWorkspace(activeWorkspace)) {
      return null
    }
    return (
      <>
        <span className="nodeSubHeader">{t('NLSChecklistDetailExternalLinks')}</span>
        <ExternalLinkSidecard
          mode="inline"
          showSyncActions={false}
          linkedOnly
          emptyLabel={t('NLSChecklistExternalLinksEmpty')}
          item={{ id: node.id, type: 'CHECKLIST_ITEM', providerMappings: node.providerMappings }}
          providers={['NOTION', 'JIRA']}
        />
      </>
    )
  }

  const renderDependenciesBody = () => (
    <div id="drawer-section-dependencies">
      <DependencyLinkList
        title={t('NLSChecklistDependencyBlockedByTitle')}
        hint={t('NLSChecklistDependencyBlockedByEmpty')}
        links={blockedByLinks}
        options={[]}
        disabled
        searchMaxLength={checklistLimits.searchMaxLength}
        onAdd={() => undefined}
        onRemove={() => undefined}
      />
      <DependencyLinkList
        title={t('NLSChecklistDependencyBlockerForTitle')}
        hint={t('NLSChecklistDependencyBlockerForEmpty')}
        links={blockerForLinks}
        options={[]}
        disabled
        searchMaxLength={checklistLimits.searchMaxLength}
        onAdd={() => undefined}
        onRemove={() => undefined}
      />
      {canManageFieldPasses && !isReadOnly && (
        <section className="nodeQualityBlock">
          <ChecklistFieldPassControl
            workspaceId={node.workspaceId}
            checklistItemId={node.id}
            checklistTitle={node.title}
            canManage
            compact
          />
        </section>
      )}
      {renderExternalLinksBlock()}
    </div>
  )

  const renderDependenciesTabBody = () => (
    <div id="drawer-section-dependencies">
      <ChecklistNodeCompletionCriteriaField
        values={view}
        limits={checklistLimits}
        disabled={inputsDisabled}
        onChange={setDraftField}
      />
      <span className="nodeSubHeader">Blockers</span>
      <DependencyLinkList
        title={t('NLSChecklistDependencyBlockedByTitle')}
        hint={t('NLSChecklistDependencyBlockedByEmpty')}
        links={blockedByLinks}
        options={[]}
        disabled
        searchMaxLength={checklistLimits.searchMaxLength}
        onAdd={() => undefined}
        onRemove={() => undefined}
      />
      <DependencyLinkList
        title={t('NLSChecklistDependencyBlockerForTitle')}
        hint={t('NLSChecklistDependencyBlockerForEmpty')}
        links={blockerForLinks}
        options={[]}
        disabled
        searchMaxLength={checklistLimits.searchMaxLength}
        onAdd={() => undefined}
        onRemove={() => undefined}
      />
      {canManageFieldPasses && !isReadOnly && (
        <>
          <span className="nodeSubHeader">Field pass</span>
          <ChecklistFieldPassControl
            workspaceId={node.workspaceId}
            checklistItemId={node.id}
            checklistTitle={node.title}
            canManage
            compact
          />
        </>
      )}
      {renderExternalLinksBlock()}
    </div>
  )

  const activityChip = <CountBadge value={comments.length} max={99} className="nodeStatusChip" />

  // Verification-gate block. Historically inside the discussion sheet; now
  // rendered inside the Setup tab because the two checkboxes describe HOW
  // the item is validated, not the conversation about it.
  const renderVerificationGateBlock = () => (
    <section className="nodeVerificationGate">
      <div className="nodeVerificationGateToggles">
        <Toggle
          wrapperClassName={`nodeVerificationToggle ${view.requiresLocationPin === true ? 'isActive' : ''}`}
          label={
            <>
              <span className="nodeVerificationToggleIcon" aria-hidden="true">
                <MyLocationRoundedIcon fontSize="inherit" />
              </span>
              <span className="nodeVerificationToggleBody">
                <strong>{t('NLSChecklistProofEnforceLocation')}</strong>
                <small>Requires a location pin on completion.</small>
              </span>
            </>
          }
          checked={view.requiresLocationPin === true}
          disabled={inputsDisabled}
          onChange={(checked) => setDraftField({ requiresLocationPin: Boolean(checked) })}
        />
        <Toggle
          wrapperClassName={`nodeVerificationToggle ${view.requiresProofFile === true ? 'isActive' : ''}`}
          label={
            <>
              <span className="nodeVerificationToggleIcon" aria-hidden="true">
                <AttachFileRoundedIcon fontSize="inherit" />
              </span>
              <span className="nodeVerificationToggleBody">
                <strong>{t('NLSChecklistProofFileRequired')}</strong>
                <small>Requires an uploaded file to close this item.</small>
              </span>
            </>
          }
          checked={view.requiresProofFile === true}
          disabled={inputsDisabled}
          onChange={(checked) => setDraftField({ requiresProofFile: Boolean(checked) })}
        />
      </div>
      {(view.requiresLocationPin === true || view.requiresProofFile === true) && (
        <div id="drawer-section-approver" className="nodeApproverPicker" data-testid="node-approver-picker">
          {/* Div wrapper — see Assignee above; a <label> re-triggers click on
           *  the Select's inner button, which would close the dropdown
           *  immediately after the user opens it. */}
          <div className="nodeDetailField">
            <span>
              {t('NLSChecklistProofApprover')}
              <em className="nodeApproverHint"> · {t('NLSChecklistProofOptional')}</em>
            </span>
            <Select
              trigger="pill"
              ariaLabel={t('NLSChecklistAriaProofApprover')}
              value={view.proofApproverId ?? ''}
              disabled={inputsDisabled || !operationalPermissions.assign}
              options={[
                { value: '', label: t('NLSChecklistProofAutoComplete') },
                ...workspaceMembers.map((member) => ({
                  value: member.userId,
                  label: `${member.displayName} (${member.role.toLocaleLowerCase()})`,
                })),
              ]}
              onChange={(value) => {
                const approver = workspaceMembers.find((member) => member.userId === value) ?? null
                setDraftField({
                  proofApproverId: approver ? approver.userId : null,
                  proofApproverName: approver ? approver.displayName : null,
                } as Partial<BoardChecklistItem>)
              }}
            />
          </div>
          <p className="nodeApproverHelp">
            {view.proofApproverId
              ? t('NLSChecklistProofApproverHelpAssigned')
              : t('NLSChecklistProofApproverHelpAuto')}
          </p>
        </div>
      )}
    </section>
  )

  const renderActivityBody = () => (
    <>
      <div className="nodeCommentsList">
        {comments.length === 0 ? (
          <p>{t('NLSChecklistDetailNoDiscussion')}</p>
        ) : (
          comments.map((comment) => (
            <NodeDetailCommentCard
              key={comment.id}
              comment={comment}
              timestampLabel={new Date(comment.timestamp).toLocaleString()}
              currentUserId={currentUser?.uid ?? null}
              canReply={!isReadOnly && operationalPermissions.comment}
              canEdit={Boolean(operationalPermissions.comment && comment.userId === currentUser?.uid)}
              canDelete={Boolean(operationalPermissions.comment && (comment.userId === currentUser?.uid || roleCanPerform(activeRole, 'ADMIN')))}
              canReact={Boolean(currentUser && operationalPermissions.comment)}
              onReply={() => {
                setReplyToCommentId(comment.id)
                setEditingCommentId(null)
                setCommentDraft('')
              }}
              onEdit={() => {
                setEditingCommentId(comment.id)
                setReplyToCommentId(null)
                setCommentDraft(comment.text)
              }}
              onDelete={() => void softDeleteComment(comment)}
              onToggleReaction={(emoji) => void toggleReaction(comment, emoji)}
            />
          ))
        )}
      </div>
      {(replyToCommentId || editingCommentId) && (
        <div className="nodeCommentComposerContext">
          <span>{editingCommentId ? t('NLSChecklistDetailCommentEditing') : t('NLSChecklistDetailCommentReplying')}</span>
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => {
              setReplyToCommentId(null)
              setEditingCommentId(null)
              setCommentDraft('')
            }}
          >
            {t('NLSCommonActionCancel')}
          </Button>
        </div>
      )}
      <div className="nodeCommentComposer">
        {operationalDisabledReason && !operationalPermissions.comment && (
          <p className="nodePermissionHint nodePermissionHintInline">{operationalDisabledReason}</p>
        )}
        <Textarea
          value={commentDraft}
          disabled={isCommentSending || isReadOnly || !currentUser || !operationalPermissions.comment}
          placeholder={t('NLSChecklistDetailPostPlaceholder')}
          maxLength={checklistLimits.commentMaxLength}
          onChange={handleCommentDraftChange}
        />
        <IconButton
          variant="primary"
          disabled={isCommentSending || isReadOnly || (!commentDraft.trim() && commentAttachmentFiles.length === 0) || !currentUser || !operationalPermissions.comment}
          label={t('NLSChecklistAriaPostUpdate')}
          icon={isCommentSending ? <MiniLoader tone={commentAttachmentFiles.length > 0 ? 'upload' : 'default'} size="xs" /> : <SendRoundedIcon fontSize="inherit" />}
          onClick={() => void sendComment()}
        />
      </div>
      {isCommentSending && (
        <div className="nodeCommentMiniStatus">
          <MiniLoader tone={commentAttachmentFiles.length > 0 ? 'upload' : 'default'} size="sm" label={commentAttachmentFiles.length > 0 ? t('NLSChecklistDetailUploadingAttachments') : t('NLSChecklistDetailPostingUpdate')} />
        </div>
      )}
      {!editingCommentId && (
        <div className="nodeCommentAttachmentPicker">
          <HiddenFileInput
            ref={commentFileInputRef}
            multiple
            disabled={isCommentSending || isReadOnly || !operationalPermissions.comment || commentAttachmentFiles.length >= MAX_CHECKLIST_COMMENT_ATTACHMENTS}
            onFiles={(files) => {
              const fileList = Array.from(files).slice(0, MAX_CHECKLIST_COMMENT_ATTACHMENTS)
              setCommentAttachmentFiles(fileList)
            }}
          />
          <Button
            variant="tertiary"
            size="sm"
            disabled={isCommentSending || isReadOnly || !operationalPermissions.comment || commentAttachmentFiles.length >= MAX_CHECKLIST_COMMENT_ATTACHMENTS}
            icon={<AttachFileRoundedIcon fontSize="inherit" />}
            onClick={() => commentFileInputRef.current?.click()}
          >
            {t('NLSChecklistActionAttachFiles')} ({commentAttachmentFiles.length}/{MAX_CHECKLIST_COMMENT_ATTACHMENTS})
          </Button>
          {commentAttachmentFiles.map((file, index) => (
            <IconButton
              key={`${file.name}-${index}`}
              variant="tertiary"
              size="sm"
              tone="danger"
              disabled={isCommentSending}
              label={t('NLSCommonFilePickerRemoveFile', { name: file.name })}
              icon={isCommentSending ? <MiniLoader tone="upload" size="xs" /> : <CloseRoundedIcon fontSize="inherit" />}
              onClick={() => setCommentAttachmentFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}
            />
          ))}
        </div>
      )}
      {mentionQuery !== null && (
        <div className="nodeMentionPicker">
          {workspaceMembers
            .filter((member) => member.displayName.toLocaleLowerCase().includes(mentionQuery.toLocaleLowerCase()))
            .slice(0, 8)
            .map((member) => (
              <Pressable key={member.userId} onClick={() => insertMention(member)}>
                <strong>{member.displayName}</strong>
                <span>{member.role.toLocaleLowerCase()}</span>
              </Pressable>
            ))}
        </div>
      )}
      <div className="nodeActivityTimeline">
        <strong>{t('NLSChecklistDetailActivityHistory')}</strong>
        {activityEntries.length === 0 ? <p>{t('NLSChecklistDetailNoRecordedActivity')}</p> : activityEntries.map((entry) => (
          <article key={entry.id}>
            <span>{entry.summary}</span>
            <small>{entry.actorName}{entry.createdAt ? ` · ${new Date(entry.createdAt).toLocaleString()}` : ''}</small>
          </article>
        ))}
      </div>
    </>
  )

  const renderVerificationBody = (_defaultOpen: boolean) => (
    <>
      {operationalDisabledReason && (!operationalPermissions.proof || !operationalPermissions.memo) && (
        <p className="nodePermissionHint nodePermissionHintInline">{operationalDisabledReason}</p>
      )}
      <section className="nodeAudioMemoBlock nodeWaveMemoBlock">
        <div className="nodeWaveMemoBar">
          <IconButton
            variant="secondary"
            disabled={isReadOnly || !operationalPermissions.memo}
            label={isAudioRecording ? t('NLSChecklistProofStopRecording') : node.voiceMemoBlobUrl ? t('NLSChecklistProofPlayMemo') : t('NLSChecklistProofRecordMemo')}
            icon={isAudioRecording ? (
              <StopRoundedIcon fontSize="inherit" />
            ) : node.voiceMemoBlobUrl ? (
              <PlayArrowRoundedIcon fontSize="inherit" />
            ) : (
              <FiberManualRecordRoundedIcon fontSize="inherit" />
            )}
            onClick={() => {
              if (isAudioRecording) stopAudioRecording()
              else void startAudioRecording()
            }}
          />
          <div className="nodeWaveBars" aria-hidden="true">
            {Array.from({ length: 22 }).map((_, index) => (
              <span key={index} style={{ height: `${8 + ((index * 7) % 18)}px` }} />
            ))}
          </div>
          <strong>{isAudioRecording ? t('NLSChecklistProofVoiceRecording') : node.voiceMemoBlobUrl ? '0:30 memo' : t('NLSChecklistProofVoiceNoMemo')}</strong>
        </div>
        {node.voiceMemoBlobUrl && <audio className="nodeVoiceMemoPlayer" src={node.voiceMemoBlobUrl} controls />}
      </section>

      <section id="drawer-section-proof" className="nodeQualityBlock">
        <div className="nodeQualityBlockHeader">
          <span>{t('NLSChecklistProofFile')}</span>
        </div>
        <HiddenFileInput
          ref={proofFileInputRef}
          disabled={isReadOnly || !operationalPermissions.proof}
          onFiles={handleProofFiles}
        />
        <Pressable
          className={`nodeProofDropZone ${node.requiresProofFile && !node.proofFileUrl ? 'isRequired' : ''}`}
          disabled={isReadOnly || !operationalPermissions.proof}
          onClick={() => proofFileInputRef.current?.click()}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleProofDrop}
        >
          <span>
            <AttachFileRoundedIcon fontSize="inherit" /> {t('NLSChecklistProofDropFile')}
          </span>
        </Pressable>
        {node.proofFileUrl && (
          <div className="nodeProofBadge">
            <span>{node.proofFileName ?? t('NLSChecklistProofFileAttached')}</span>
            <IconButton
              variant="tertiary"
              size="sm"
              tone="danger"
              disabled={isReadOnly || !operationalPermissions.proof}
              label={t('NLSChecklistProofRemoveProof')}
              icon={<CloseRoundedIcon fontSize="inherit" />}
              onClick={() =>
                void onVerificationChange(node, buildChecklistProofRemovalUpdates())
              }
            />
          </div>
        )}
      </section>

      <section id="drawer-section-location" className="nodeQualityBlock">
        <div className="nodeQualityBlockHeader">
          <span>{t('NLSChecklistProofLocationGate')}</span>
        </div>
        <Button
          variant="tertiary"
          size="sm"
          disabled={isReadOnly || !roleCanPerform(activeRole, 'ADMIN') || isRecurringOccurrenceScoped}
          icon={<MyLocationRoundedIcon fontSize="inherit" />}
          onClick={captureTargetLocation}
        >
          {t('NLSChecklistActionCaptureCurrentLocation')}
        </Button>
        {node.targetCoordinates && (
          <p>
            {node.targetCoordinates.lat.toFixed(5)}, {node.targetCoordinates.lng.toFixed(5)}
          </p>
        )}
        {locationNotice && <p>{locationNotice}</p>}
      </section>

      <section className="nodeQualityBlock">
        <div className="nodeQualityBlockHeader">
          <span>{t('NLSChecklistProofVideoMemo')}</span>
          {node.proofVideoUrl && <strong>{t('NLSChecklistProofRecorded')}</strong>}
        </div>
        <Button
          variant="tertiary"
          size="sm"
          disabled={isReadOnly || !operationalPermissions.memo}
          icon={<VideocamRoundedIcon fontSize="inherit" />}
          onClick={() => void openRecorder()}
        >
          {t('NLSChecklistActionRecordVideoMemo')}
        </Button>
        {node.proofVideoUrl && <video className="nodeVideoMemoPreview" src={node.proofVideoUrl} controls />}
      </section>

      {node.assigneeId && (
        <section className="nodeQualityBlock nodeVouchBlock">
          <div className="nodeQualityBlockHeader">
            <span>{t('NLSChecklistProofPeerVerification')}</span>
            <strong>{assignedHelper?.displayName ?? node.assigneeName ?? t('NLSChecklistProofAssignedHelper')}</strong>
          </div>
          <p>
            {t('NLSChecklistProofPeerVerificationHelp')}
          </p>
          <Button
            variant="tertiary"
            size="sm"
            disabled={!canVouchForAssignee || vouchBusy}
            loading={vouchBusy}
            onClick={() => void handleVouch()}
          >
            {vouchBusy ? t('NLSChecklistProofRecording') : t('NLSChecklistActionVouchCompletion')}
          </Button>
          {!canVouchForAssignee && (
            <p className="nodePermissionHint nodePermissionHintInline">{t('NLSChecklistProofThirdPartyRequired')}</p>
          )}
          {vouchNotice && <p>{vouchNotice}</p>}
        </section>
      )}
    </>
  )

  const handleAssignmentChange = withLock(async (assignee: ChecklistMemberOption | null) => {
    await onAssignmentChange(node, assignee)
  })

  const titleFieldProps = {
    titleValue: titleDraft,
    limits: checklistLimits,
    disabled: inputsDisabled,
    onTitleChange: withLock((next: string) => {
      setTitleDraft(next)
    }),
    onTitleCommit: () => setDraftField({ title: truncateToLimit(titleDraft, checklistLimits.titleMaxLength) }),
  }

  const descriptionFieldProps = {
    values: view,
    limits: checklistLimits,
    disabled: inputsDisabled,
    onChange: setDraftField,
  }

  const stockTabVisible = CHECKLIST_DETAIL_SECTIONS.stock.visible && Boolean(activeWorkspace && canViewStockRequirements)
  const hasRiskMetadata = Boolean(
    (typeof view.complexityStars === 'number' && view.complexityStars > 0)
    || (typeof view.urgencyStars === 'number' && view.urgencyStars > 0)
    || (typeof view.importanceStars === 'number' && view.importanceStars > 0)
    || (typeof view.estimatedHours === 'number' && view.estimatedHours > 0)
    || (typeof view.actualCost === 'number' && view.actualCost > 0)
    || normalizeWeightage(view.weightage) > 1,
  )
  const detailsHasValue = Boolean(view.description?.trim() || hasRiskMetadata)
  const detailsChip = (
    <StatusChip tone={detailsHasValue ? 'info' : 'muted'} className="nodeStatusChip">
      {detailsHasValue ? t('NLSChecklistDetailSet') : t('NLSChecklistDetailEmpty')}
    </StatusChip>
  )
  const detailsChipText = view.description?.trim() || hasRiskMetadata ? t('NLSChecklistDetailSet') : t('NLSChecklistDetailEmpty')
  const proofChip = (
    <StatusChip tone={node.requiresProofFile ? 'warning' : 'neutral'} className="nodeStatusChip">
      {node.requiresProofFile ? t('NLSChecklistDetailProofGated') : t('NLSChecklistStatusOpen')}
    </StatusChip>
  )
  const proofChipText = node.requiresProofFile ? t('NLSChecklistDetailProofGated') : t('NLSChecklistStatusOpen')
  const setupHasActiveGate = view.requiresLocationPin === true || view.requiresProofFile === true
  const setupHasCustomCard = Boolean(node.cardBackgroundUrl)
  const setupSignals = (setupHasActiveGate ? 1 : 0) + (setupHasCustomCard ? 1 : 0) + (stockTabVisible ? 1 : 0)
  const appearanceChipLabel = setupSignals === 0 ? 'Default' : setupSignals === 1 ? '1 signal' : `${setupSignals} signals`
  const appearanceChip = (
    <StatusChip tone={setupSignals > 0 ? 'info' : 'muted'} className="nodeStatusChip">
      {appearanceChipLabel}
    </StatusChip>
  )
  const appearanceChipText = appearanceChipLabel

  const renderCoreFields = () => (
    <ChecklistNodeCoreFields
      {...titleFieldProps}
      {...descriptionFieldProps}
    />
  )

  const renderPinnedFields = () => (
    <div className="nodeDetailPinned">
      <ChecklistNodeTitleField {...titleFieldProps} />
    </div>
  )

  const renderOwnershipGrid = () => (
    <div className="nodeOwnershipGrid">
      <label className="nodeDetailField">
        <span>{t('NLSChecklistDetailCreatedBy')}</span>
        <TextInput value={node.createdByName || 'Workspace admin'} disabled />
      </label>
      {/* Div-wrapped, not <label> — a <label> synthesises a second click
       *  on the first labelable descendant (the Select's trigger button),
       *  which immediately re-toggles the popover shut. That reads as
       *  "clicking the Assignee dropdown does nothing" and hits admins
       *  hardest because the disabled path (for helpers / read-only) was
       *  masking the same UX for other roles. */}
      <div id="drawer-section-assignee" className="nodeDetailField">
        <span>{t('NLSChecklistDetailAssignee')}</span>
        <Select
          trigger="pill"
          ariaLabel={t('NLSChecklistDetailAssigneeAria')}
          value={view.assigneeId ?? ''}
          disabled={isReadOnly || !currentUser || !operationalPermissions.assign}
          options={[{ value: '', label: t('NLSChecklistDetailUnassigned') }, ...workspaceMembers.map((member) => ({ value: member.userId, label: `${member.displayName} (${member.role.toLocaleLowerCase()})` }))]}
          onChange={(value) => {
            const assignee = workspaceMembers.find((member) => member.userId === value) ?? null
            if (isNewNode) {
              const shouldPromoteToAssigned = Boolean(assignee?.userId)
                && !view.isCompleted
                && (!view.workflowStatus || view.workflowStatus === 'CREATED')
              setDraftField({
                assigneeId: assignee?.userId ?? null,
                assigneeName: assignee?.displayName ?? null,
                ...(shouldPromoteToAssigned ? { workflowStatus: 'ASSIGNED', isCompleted: false } : {}),
              } as Partial<BoardChecklistItem>)
              return
            }
            if (isRecurringOccurrenceScoped) {
              // Defer the write until the user picks a scope. The
              // dialog routes through onAssignmentChange with an
              // explicit scope so 'global' writes land on the
              // definition (VIEWER-friendly) instead of a per-page
              // detailPatch (EDITOR-only).
              setPendingRecurringAssignee({ assignee })
              return
            }
            void handleAssignmentChange(assignee)
          }}
        />
      </div>
    </div>
  )

  const renderDetailsBody = () => (
    <>
      <ChecklistNodeCompletionCriteriaField
        values={view}
        limits={checklistLimits}
        disabled={inputsDisabled}
        onChange={setDraftField}
      />
      {(node as { type?: string }).type === 'GOAL' && (
        <GoalClientPicker
          workspaceId={node.workspaceId || activeWorkspace?.id || ''}
          clientId={(view as { clientId?: string | null }).clientId}
          onChange={(clientId) => setDraftField({ clientId } as Partial<BoardChecklistItem>)}
          disabled={inputsDisabled}
        />
      )}
      <ChecklistNodeRiskSection
        nodeId={node.id}
        workspaceId={node.workspaceId || activeWorkspace?.id}
        values={view}
        limits={checklistLimits}
        disabled={inputsDisabled}
        disableScheduleFields={isRecurringOccurrenceScoped}
        wrapper="none"
        impactLabel={impactLabel}
        onChange={setDraftField}
        onRatingSelect={handleRatingSelect}
      />
    </>
  )

  const renderDetailsTabBody = () => (
    <>
      <ChecklistNodeDescriptionField {...descriptionFieldProps} />
      {renderOwnershipGrid()}
      {(node as { type?: string }).type === 'GOAL' && (
        <GoalClientPicker
          workspaceId={node.workspaceId || activeWorkspace?.id || ''}
          clientId={(view as { clientId?: string | null }).clientId}
          onChange={(clientId) => setDraftField({ clientId } as Partial<BoardChecklistItem>)}
          disabled={inputsDisabled}
        />
      )}
      <span className="nodeSubHeader">Risk & Impact</span>
      <ChecklistNodeRiskSection
        nodeId={node.id}
        workspaceId={node.workspaceId || activeWorkspace?.id}
        values={view}
        limits={checklistLimits}
        disabled={inputsDisabled}
        disableScheduleFields={isRecurringOccurrenceScoped}
        wrapper="none"
        impactLabel={impactLabel}
        onChange={setDraftField}
        onRatingSelect={handleRatingSelect}
      />
    </>
  )

  const renderProofAndAttachmentsBody = () => (
    <>
      <ItemAttachmentsPanel
        target={{
          id: node.id,
          workspaceId: node.workspaceId,
          title: liveChecklistDoc?.title ?? node.title,
          kind: 'CHECKLIST',
          collectionName: 'checklists',
          attachedFormIds: liveChecklistDoc?.attachedFormIds ?? node.attachedFormIds,
          attachedDesignIds: liveChecklistDoc?.attachedDesignIds ?? node.attachedDesignIds,
          attachedUploadIds: liveChecklistDoc?.attachedUploadIds ?? node.attachedUploadIds,
          attachedLinkIds: liveChecklistDoc?.attachedLinkIds ?? node.attachedLinkIds,
          assigneeIds: node.assigneeId ? [node.assigneeId] : [],
        }}
        budgetLimit={effectiveBudgetLimit}
        estimatedCost={effectiveEstimatedCost}
        canEdit={canEdit && !isReadOnly && !isNewNode && !isRecurringOccurrenceScoped}
        onSetBudgetLimit={budgetEnabled ? () => setActiveTabId('financials') : undefined}
      />
      {renderVerificationBody(isViewer)}
    </>
  )

  const renderAppearanceBody = (includeStock = false) => (
    <>
      <span className="nodeSubHeader">Location</span>
      <NodeDetailLocationBreadcrumb
        segments={resolvedBreadcrumb}
        className="nodeDetailLocationBreadcrumb"
        editable={Boolean(onReparent && parentPickerCandidates)}
        onReparent={onReparent}
        parentPickerContext={onReparent && parentPickerCandidates ? {
          checklistType: node.checklistType === 'DAILY' || node.checklistType === 'WEEKLY'
            ? node.checklistType
            : 'INDIVIDUAL',
          window: node.scheduleStartDate && node.scheduleEndDate
            ? { start: node.scheduleStartDate.slice(0, 10), end: node.scheduleEndDate.slice(0, 10) }
            : null,
          candidates: parentPickerCandidates,
          workspaceDefaultContainerId: parentPickerWorkspaceDefaultId ?? '',
          currentParentId: node.parentContainerId ?? null,
        } : undefined}
      />
      {Boolean(node.repeatPaused) && (
        <ControlNotice
          tone="warning"
          message={t('NLSChecklistRepeatPausedParentDates')}
        />
      )}
      <span className="nodeSubHeader">Completion gates</span>
      {renderVerificationGateBlock()}

      <span className="nodeSubHeader">Card background</span>
      <section className="nodeQualityBlock nodeCardBackgroundBlock">
        <div className="nodeQualityBlockHeader">
          <span>Card background</span>
          <StatusChip tone="neutral" size="sm" className="nodeStatusChip">
            {node.cardBackgroundUrl ? 'Custom' : 'Default'}
          </StatusChip>
        </div>
        <div className="nodeCardBackgroundRow">
          <div
            className={`nodeCardBackgroundPreview ${node.cardBackgroundUrl ? 'hasImage' : ''}`}
            style={node.cardBackgroundUrl ? { backgroundImage: `url("${node.cardBackgroundUrl}")` } : undefined}
            aria-hidden="true"
          />
          <div className="nodeCardBackgroundMeta">
            <strong>{node.cardBackgroundFileName ?? 'Parent-colour texture'}</strong>
            <small>PNG or JPG, up to 4 MB</small>
          </div>
          <div className="nodeCardBackgroundActions">
            <HiddenFileInput
              ref={bgFileInputRef}
              accept="image/*"
              disabled={!canEdit || isReadOnly || isNewNode || isRecurringOccurrenceScoped || !cardBackgroundsEnabled || cardBackgroundBusy}
              onFiles={(files) => {
                const file = files[0]
                if (file) void persistCardBackground(file)
              }}
            />
            <IconButton
              variant="tertiary"
              size="sm"
              aria-label={isNewNode ? 'Save item before uploading background' : !cardBackgroundsEnabled ? 'Card backgrounds disabled' : `Upload ${CARD_BACKGROUND_CATEGORY_LABEL}`}
              label={isNewNode ? 'Save item before uploading background' : !cardBackgroundsEnabled ? 'Card backgrounds disabled' : `Upload ${CARD_BACKGROUND_CATEGORY_LABEL}`}
              disabled={!canEdit || isReadOnly || isNewNode || isRecurringOccurrenceScoped || !cardBackgroundsEnabled || cardBackgroundBusy}
              icon={<AttachFileRoundedIcon fontSize="inherit" />}
              onClick={() => bgFileInputRef.current?.click()}
            />
            {node.cardBackgroundUrl && (
              <IconButton
                variant="tertiary"
                size="sm"
                disabled={!canEdit || isReadOnly || isNewNode || isRecurringOccurrenceScoped || !cardBackgroundsEnabled || cardBackgroundBusy}
                label="Remove card background"
                icon={<DeleteOutlineRoundedIcon fontSize="inherit" />}
                onClick={() => void clearCardBackground()}
              />
            )}
          </div>
        </div>
      </section>
      {includeStock && stockTabVisible && (
        <>
          <span className="nodeSubHeader">Stock requirements</span>
          {renderStockBody()}
        </>
      )}
    </>
  )

  const renderStockBody = () => (
    <NodeStockTab
      workspaceId={node.workspaceId}
      requiredStockIds={view.requiredStockIds}
      stockRequirements={view.stockRequirements}
      consumptionMode={view.consumptionMode}
      disabled={isReadOnly || (isNewNode && inputsDisabled)}
      onChange={withLock(({ requiredStockIds, stockRequirements, consumptionMode }) => {
        if (isNewNode || isRecurringOccurrenceScoped) {
          setDraftField({ requiredStockIds, stockRequirements, consumptionMode } as Partial<BoardChecklistItem>)
          return
        }
        const beforeIds = stockRequirementIds(normalizeStockRequirements(view))
        const afterIds = stockRequirementIds(normalizeStockRequirements({ requiredStockIds, stockRequirements }))
        void writeChecklistUpdate(
          node.id,
          { requiredStockIds, stockRequirements, consumptionMode },
          { uid: currentUser?.uid ?? '', displayName: currentDisplayName || currentUser?.email || null },
        ).then(() => syncStockTaskLinks(node.workspaceId, node.id, beforeIds, afterIds))
          .catch((error) => console.warn('[NodeDetailDrawer] stock link update failed', error))
      })}
    />
  )

  const visibleTabIds = useMemo<ChecklistDetailSectionId[]>(() => {
    const ids: ChecklistDetailSectionId[] = []
    if (CHECKLIST_DETAIL_SECTIONS.details.visible) ids.push('details')
    if (CHECKLIST_DETAIL_SECTIONS.financials.visible && budgetEnabled) ids.push('financials')
    if (CHECKLIST_DETAIL_SECTIONS.dependencies.visible) ids.push('dependencies')
    if (CHECKLIST_DETAIL_SECTIONS.proofAndAttachments.visible) ids.push('proofAndAttachments')
    if (CHECKLIST_DETAIL_SECTIONS.appearance.visible) ids.push('appearance')
    return ids
  }, [budgetEnabled])

  useEffect(() => {
    if (visibleTabIds.length > 0 && !visibleTabIds.includes(activeTabId)) {
      setActiveTabId(visibleTabIds[0])
    }
  }, [activeTabId, visibleTabIds])

  useEffect(() => {
    if (tabPanelRef.current) tabPanelRef.current.scrollTop = 0
  }, [activeTabId])

  useEffect(() => {
    if (!discussionSheetOpen) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDiscussionSheetOpen(false)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [discussionSheetOpen])

  useEffect(() => {
    if (!shouldUseTabbedPopup && discussionSheetOpen) setDiscussionSheetOpen(false)
  }, [discussionSheetOpen, shouldUseTabbedPopup])

  const tabChipById: Partial<Record<ChecklistDetailSectionId, ReactNode>> = {
    details: detailsChipText,
    financials: financialsChipText,
    dependencies: isBlocked ? `${savedOpenBlockerTitles.length || 1} blocker${savedOpenBlockerTitles.length === 1 ? '' : 's'}` : t('NLSChecklistStatusClear'),
    proofAndAttachments: proofChipText,
    appearance: appearanceChipText,
  }

  const tabIconById: Pick<Record<ChecklistDetailSectionId, NodeDetailTab>, 'details' | 'financials' | 'dependencies' | 'proofAndAttachments' | 'appearance'> = {
    details: { id: 'details', label: sectionLabel('details'), icon: DescriptionOutlinedIcon, iconActive: DescriptionRoundedIcon },
    financials: { id: 'financials', label: sectionLabel('financials'), icon: PaymentsOutlinedIcon, iconActive: PaymentsRoundedIcon },
    dependencies: { id: 'dependencies', label: sectionLabel('dependencies'), icon: AccountTreeOutlinedIcon, iconActive: AccountTreeRoundedIcon },
    proofAndAttachments: { id: 'proofAndAttachments', label: sectionLabel('proofAndAttachments'), icon: VerifiedOutlinedIcon, iconActive: VerifiedRoundedIcon },
    appearance: { id: 'appearance', label: 'Setup', icon: PaletteOutlinedIcon, iconActive: PaletteRoundedIcon, tourId: 'node-stock-section' },
  }

  const visibleTabs: NodeDetailTab[] = visibleTabIds.map((id) => ({
    ...tabIconById[id as keyof typeof tabIconById],
    chip: tabChipById[id],
  }))

  const stackedSections: ChecklistPanelSection[] = [
    {
      id: 'details',
      title: sectionLabel('details'),
      icon: <DescriptionOutlinedIcon fontSize="inherit" />,
      chip: detailsChip,
      defaultOpen: true,
      hidden: !CHECKLIST_DETAIL_SECTIONS.details.visible,
      body: (
        <>
          {renderCoreFields()}
          {renderOwnershipGrid()}
          {renderDetailsBody()}
        </>
      ),
    },
    {
      id: 'financials',
      title: sectionLabel('financials'),
      icon: <PaymentsOutlinedIcon fontSize="inherit" />,
      chip: financialsChip,
      defaultOpen: false,
      hidden: !CHECKLIST_DETAIL_SECTIONS.financials.visible || !budgetEnabled,
      body: renderFinancialsBody(),
    },
    {
      id: 'dependencies',
      title: t('NLSChecklistDetailDependencies'),
      icon: <AccountTreeOutlinedIcon fontSize="inherit" />,
      chip: dependenciesChip,
      defaultOpen: false,
      hidden: !CHECKLIST_DETAIL_SECTIONS.dependencies.visible,
      body: renderDependenciesBody(),
    },
    {
      id: 'proofAndAttachments',
      title: sectionLabel('proofAndAttachments'),
      icon: <VerifiedOutlinedIcon fontSize="inherit" />,
      chip: proofChip,
      defaultOpen: false,
      hidden: !CHECKLIST_DETAIL_SECTIONS.proofAndAttachments.visible,
      body: renderProofAndAttachmentsBody(),
    },
    {
      id: 'appearance',
      title: sectionLabel('appearance'),
      icon: <PaletteOutlinedIcon fontSize="inherit" />,
      chip: appearanceChip,
      defaultOpen: false,
      hidden: !CHECKLIST_DETAIL_SECTIONS.appearance.visible,
      body: renderAppearanceBody(),
    },
    {
      id: 'discussion',
      title: t('NLSChecklistDetailActivityDiscussion'),
      icon: <ForumOutlinedIcon fontSize="inherit" />,
      chip: activityChip,
      defaultOpen: false,
      hidden: !CHECKLIST_DETAIL_SECTIONS.discussion.visible,
      body: renderActivityBody(),
    },
    {
      id: 'stock',
      title: sectionLabel('stock'),
      icon: <AccountTreeOutlinedIcon fontSize="inherit" />,
      defaultOpen: false,
      hidden: !CHECKLIST_DETAIL_SECTIONS.stock.visible || !activeWorkspace || !canViewStockRequirements,
      body: renderStockBody(),
    },
  ]

  const renderActiveTab = () => {
    switch (activeTabId) {
      case 'financials':
        return renderFinancialsBody()
      case 'dependencies':
        return renderDependenciesTabBody()
      case 'proofAndAttachments':
        return renderProofAndAttachmentsBody()
      case 'appearance':
        return renderAppearanceBody(true)
      case 'recurrence':
      case 'details':
      default:
        return renderDetailsTabBody()
    }
  }

  const activePanelDomIds = getNodeDetailTabDomIds(activeTabId)

  return (
    <>
      {!isInlinePresentation && (
        <motion.div
          className={`nodeDetailOverlay ${isPopupPresentation ? 'nodeDetailOverlayPopup' : ''} themeOverlay fixed inset-0`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleCloseRequest}
        />
      )}
      <motion.aside
        className={`nodeDetailDrawer nodeDetailDrawerSheet NodeDetailDrawer--v2 ${isPopupPresentation ? 'nodeDetailDrawerPopup' : ''} ${isInlinePresentation ? 'nodeDetailDrawerInline' : ''} chkPanel chkPanel--drawer chkPanel--accent-aurora chkPanel--adopted ${editLock.heldByMe ? 'isEditing' : 'isSaved'}`}
        initial={isInlinePresentation ? undefined : 'hidden'}
        animate={isInlinePresentation ? undefined : 'visible'}
        exit={isInlinePresentation ? undefined : 'exit'}
        variants={sheetVariants}
        drag={!isPopupPresentation && !isInlinePresentation && isMobileViewport ? 'y' : false}
        dragConstraints={{ top: 0, bottom: MOBILE_SHEET_DRAG_LIMIT_PX }}
        dragElastic={0.08}
        onDragEnd={handleSheetDragEnd}
      >
        {!isInlinePresentation && <div className="mobileSheetHandle" aria-hidden="true" />}
        {(() => {
          const { book: bookSegment } = splitBookSegment(resolvedBreadcrumb)
          return (
            <>
              <header className="nodeDetailHeader nodeDetailActionBar">
                {(() => {
                  const editLockHolder = (!isNewNode && editLock.holder && !editLock.heldByMe) ? editLock.holder : null
                  const statusDisabledReason =
                    isReadOnly
                      ? 'View-only access to this checklist'
                      : (!isNewNode && !operationalPermissions.status)
                        ? 'Your role cannot change status'
                        : editLockHolder
                          ? `Locked by ${editLockHolder}`
                          : null
                  return (
                    <StatusSelect
                      value={workflowStatus}
                      disabledReason={statusDisabledReason}
                      onChange={handleWorkflowStatusChange}
                      item={view}
                      actor={currentUser ? { uid: currentUser.uid } : null}
                      optionEval={(status) => canPickInDropdown(view, currentUser ? { uid: currentUser.uid, role: activeRole } : null, status)}
                    />
                  )
                })()}
                {bookSegment && (
                  <ChecklistBookChip segment={bookSegment} className="nodeDetailBookChip" />
                )}
                {shouldUseTabbedPopup && (
                  <Pressable
                    className={`nodeDetailDiscussionPill ${discussionSheetOpen ? 'isActive' : ''}`}
                    aria-label={`Open discussion (${comments.length} ${comments.length === 1 ? 'comment' : 'comments'})`}
                    aria-pressed={discussionSheetOpen}
                    onClick={() => setDiscussionSheetOpen((current) => !current)}
                  >
                    <ForumOutlinedIcon fontSize="inherit" />
                    <span>{comments.length}</span>
                  </Pressable>
                )}
                <div className="nodeDetailHeaderActions">
                  {!isNewNode && node?.id && (
                    <>
                      <ItemTimerButton
                        workspaceId={node.workspaceId || activeWorkspace?.id}
                        nodeId={node.id}
                        nodeLabel={node.title}
                      />
                      <ItemTimeDisplay
                        workspaceId={node.workspaceId || activeWorkspace?.id}
                        nodeId={node.id}
                        actualMinutes={node.actualMinutes}
                      />
                    </>
                  )}
                  {canEdit && (
                    editLock.holder && !editLock.heldByMe ? (
                      <Button
                        variant="tertiary"
                        size="sm"
                        className={`nodeRecheckButton ${editLock.isCheckingLock ? 'isSpinning' : ''}`}
                        onClick={() => void editLock.recheck()}
                        disabled={editLock.isCheckingLock}
                        title={`Locked by ${editLock.holder}. Click to re-check.`}
                        icon={<RefreshRoundedIcon fontSize="inherit" />}
                      >
                        Re-check
                      </Button>
                    ) : (isNewNode ? hasPendingChanges : (editLock.heldByMe && hasManualSaveDraft)) ? (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void handleSaveClick()}
                        disabled={isSavingDraft}
                        loading={isSavingDraft}
                        icon={<SaveRoundedIcon fontSize="inherit" />}
                      >
                        {isSavingDraft ? (isNewNode ? 'Creating…' : 'Saving…') : (isNewNode ? (newNodeSaveLabel ?? 'Create') : 'Save')}
                      </Button>
                    ) : null
                  )}
                  {canDelete && (
                    <IconButton
                      variant="tertiary"
                      tone="danger"
                      onClick={() => setDeleteDialogOpen(true)}
                      label={t('NLSChecklistActionDeleteNode')}
                      icon={<DeleteOutlineRoundedIcon fontSize="inherit" />}
                    />
                  )}
                  {!isInlinePresentation && (
                    <IconButton
                      variant="tertiary"
                      onClick={handleCloseRequest}
                      label={t('NLSChecklistActionClose')}
                      icon={<CloseRoundedIcon fontSize="inherit" />}
                    />
                  )}
                </div>
              </header>
            </>
          )
        })()}

        <div className={`nodeDetailBody ${shouldUseTabbedPopup ? 'nodeDetailBodyTabbed' : ''}`}>
          <StatusBlockerBanner
            pendingStatus={pendingStatus ?? null}
            blockers={pendingBlockers ?? []}
            onDismiss={() => onPendingStatusDismiss?.()}
            onBlockerClick={scrollToSection}
          />
          {(operationalDisabledReason || completionDisabledReason || permissionNotice) && (
            <div className="nodePermissionHint" role="status">
              {permissionNotice ?? operationalDisabledReason ?? completionDisabledReason}
            </div>
          )}
          {shouldUseTabbedPopup ? (
            <>
              {renderPinnedFields()}
              <NodeDetailTabStrip
                tabs={visibleTabs}
                activeId={activeTabId}
                onSelect={setActiveTabId}
              />
              <div
                ref={tabPanelRef}
                id={activePanelDomIds.panelId}
                className="nodeDetailTabPanel"
                role="tabpanel"
                aria-labelledby={activePanelDomIds.tabId}
                tabIndex={0}
              >
                {renderActiveTab()}
              </div>
              {discussionSheetOpen && (
                <>
                  <Pressable
                    className="nodeDetailDiscussionScrim"
                    aria-label="Close discussion"
                    onClick={() => setDiscussionSheetOpen(false)}
                  />
                  <aside className="nodeDetailDiscussionSheet" aria-label="Discussion">
                    <header className="nodeDetailDiscussionSheetHeader">
                      <div>
                        <span className="nodeSubHeader">Discussion</span>
                        <strong>{comments.length} {comments.length === 1 ? 'comment' : 'comments'}</strong>
                      </div>
                      <IconButton
                        variant="tertiary"
                        size="sm"
                        aria-label="Close discussion"
                        label="Close discussion"
                        icon={<CloseRoundedIcon fontSize="inherit" />}
                        onClick={() => setDiscussionSheetOpen(false)}
                      />
                    </header>
                    <div className="nodeDetailDiscussionSheetBody">
                      {renderActivityBody()}
                    </div>
                  </aside>
                </>
              )}
            </>
          ) : (
            <ChecklistPanel
              variant="inline"
              density="cozy"
              accent="none"
              sections={stackedSections}
              className="nodeDetailSectionsPanel"
            />
          )}
        </div>
      </motion.aside>

      <AppDialog
        open={deleteDialogOpen}
        title={t('NLSManageActionMoveToBin')}
        message={t('NLSChecklistDialogMoveToBinMessage')}
        onDismiss={() => setDeleteDialogOpen(false)}
        actions={[
          {
            label: t('NLSManageActionMoveToBin'),
            variant: 'danger',
            onClick: () => onDeleteNode(node).then(onClose),
          },
          { label: t('NLSCommonActionCancel'), variant: 'ghost', onClick: () => setDeleteDialogOpen(false) },
        ]}
      />

      <ConfirmActionModal
        open={Boolean(pendingDeleteAttachment)}
        title={t('NLSChecklistAttachmentDeleteTitle')}
        body="Delete attachment permanently?"
        confirmLabel={t('NLSChecklistAttachmentDeleteConfirm')}
        danger
        onClose={() => setPendingDeleteAttachment(null)}
        onConfirm={async () => {
          if (!pendingDeleteAttachment) return
          const { entry, attId } = pendingDeleteAttachment
          await deleteWorkspaceProvisionPermanently({
            workspaceId: node.workspaceId,
            kind: 'upload',
            id: attId,
            actor: {
              id: currentUser?.uid ?? '',
              name: currentDisplayName,
            },
          })
          await updateDoc(doc(firestoreDb, WORKSPACE_EXPENSES_COLLECTION, entry.id), {
            attachments: entry.attachments?.filter((id) => id !== attId) ?? [],
          })
          showToast('Attachment deleted permanently.', 'success')
          setPendingDeleteAttachment(null)
        }}
      />

      {isNewNode ? (
        <AppDialog
          open={closeDialogOpen}
          title="Unsaved new checklist"
          message="Save this checklist item before closing, discard it, or go back to keep editing."
          onDismiss={() => setCloseDialogOpen(false)}
          actions={[
            {
              label: 'Save & close',
              variant: 'primary',
              disabled: !newDraftReady || isSavingDraft,
              autoFocus: newDraftReady,
              onClick: () => {
                if (!newDraftReady) {
                  flashRatingToast('Add a title before saving.')
                  return
                }
                setSaveAndCloseAfterConfirm(true)
                if (isRecurringOccurrenceScoped) {
                  setCloseDialogOpen(false)
                  startRecurringSaveFlow(true)
                  return
                }
                setIsSavingDraft(true)
                return performSave()
                  .then(onClose)
                  .catch((error) => {
                    flashRatingToast(error instanceof Error ? error.message : 'Could not save this checklist item.')
                  })
                  .finally(() => setIsSavingDraft(false))
              },
            },
            {
              label: 'Discard',
              variant: 'danger',
              onClick: () => {
                setCloseDialogOpen(false)
                onClose()
              },
            },
            { label: 'Cancel', variant: 'ghost', onClick: () => setCloseDialogOpen(false) },
          ]}
        />
      ) : (
        <AppDialog
          open={closeDialogOpen}
          title="Unsaved changes"
          message="You have unsaved edits to this node. Save them before closing?"
          onDismiss={() => setCloseDialogOpen(false)}
          actions={[
            {
              label: 'Save & close',
              variant: 'primary',
              onClick: () => {
                if (isRecurringOccurrenceScoped) {
                  setCloseDialogOpen(false)
                  startRecurringSaveFlow(true)
                  return
                }
                setIsSavingDraft(true)
                return performSave()
                  .then(onClose)
                  .catch((error) => {
                    flashRatingToast(error instanceof Error ? error.message : 'Could not save this checklist item.')
                  })
                  .finally(() => setIsSavingDraft(false))
              },
            },
            {
              label: 'Discard & close',
              variant: 'danger',
              onClick: () => {
                void editLock.release()
                setDraft({})
                setCloseDialogOpen(false)
                onClose()
              },
            },
            { label: 'Cancel', variant: 'ghost', onClick: () => setCloseDialogOpen(false) },
          ]}
        />
      )}

      <AppDialog
        open={titleScopeDialogOpen}
        title="Update title everywhere?"
        message="This checklist title is shared across every instance of this recurring checklist. Continue to update it for all dates/weeks?"
        onDismiss={() => {
          setTitleScopeDialogOpen(false)
          setSaveAndCloseAfterConfirm(false)
        }}
        actions={[
          {
            label: hasRecurringVersionedDraft ? 'Continue' : 'Update all',
            variant: 'primary',
            autoFocus: true,
            disabled: isSavingDraft,
            loading: isSavingDraft,
            onClick: () => {
              if (hasRecurringVersionedDraft) {
                setTitleScopeDialogOpen(false)
                setRecurringScopeDialogOpen(true)
                return
              }
              return completeConfirmedSave('global')
            },
          },
          {
            label: 'Cancel',
            variant: 'ghost',
            onClick: () => {
              setTitleScopeDialogOpen(false)
              setSaveAndCloseAfterConfirm(false)
            },
          },
        ]}
      />

      <AppDialog
        open={recurringScopeDialogOpen}
        title="Apply recurring changes"
        message="Choose whether these checklist detail changes affect only this instance or this instance and the future ones."
        onDismiss={() => {
          setRecurringScopeDialogOpen(false)
          setSaveAndCloseAfterConfirm(false)
        }}
        actions={[
          {
            label: 'Only this instance',
            variant: 'secondary',
            disabled: isSavingDraft,
            onClick: () => completeConfirmedSave('single'),
          },
          {
            label: 'This and future',
            variant: 'primary',
            autoFocus: true,
            disabled: isSavingDraft,
            onClick: () => completeConfirmedSave('future'),
          },
          {
            label: 'Cancel',
            variant: 'ghost',
            onClick: () => {
              setRecurringScopeDialogOpen(false)
              setSaveAndCloseAfterConfirm(false)
            },
          },
        ]}
      />

      <AppDialog
        open={Boolean(pendingRecurringAssignee)}
        title="Apply this assignment"
        message="This checklist recurs. Choose which occurrences the new assignee should apply to."
        onDismiss={() => setPendingRecurringAssignee(null)}
        actions={[
          {
            label: 'Only this occurrence',
            variant: 'secondary',
            onClick: () => applyPendingAssignee('single'),
          },
          {
            label: 'This and future',
            variant: 'secondary',
            onClick: () => applyPendingAssignee('future'),
          },
          {
            label: 'Every occurrence',
            variant: 'primary',
            autoFocus: true,
            onClick: () => applyPendingAssignee('global'),
          },
          {
            label: 'Cancel',
            variant: 'ghost',
            onClick: () => setPendingRecurringAssignee(null),
          },
        ]}
      />

      <AppDialog
        open={notNeededScopeDialogOpen}
        title={t('NLSChecklistNotNeededTitle')}
        onDismiss={() => setNotNeededScopeDialogOpen(false)}
        actions={[
          {
            label: t('NLSChecklistNotNeededConfirm'),
            variant: 'primary',
            autoFocus: true,
            onClick: () => {
              const scope = notNeededScopeChoice === 'FROM_PERIOD' ? 'FROM_PERIOD' : null
              setNotNeededScopeDialogOpen(false)
              void onStatusChange(node, 'NOT_REQUIRED', { notRequiredScope: scope })
            },
          },
          {
            label: 'Cancel',
            variant: 'ghost',
            onClick: () => setNotNeededScopeDialogOpen(false),
          },
        ]}
      >
        <SettingsRadioList
          name="not-needed-scope"
          value={notNeededScopeChoice}
          onChange={(val) => setNotNeededScopeChoice(val as 'THIS_PERIOD' | 'FROM_PERIOD')}
          options={
            node.checklistType === 'WEEKLY'
              ? [
                  { value: 'THIS_PERIOD', label: t('NLSChecklistNotNeededJustThisWeek') },
                  { value: 'FROM_PERIOD', label: t('NLSChecklistNotNeededFromThisWeekOn') },
                ]
              : [
                  { value: 'THIS_PERIOD', label: t('NLSChecklistNotNeededJustThisDay') },
                  { value: 'FROM_PERIOD', label: t('NLSChecklistNotNeededFromTodayOn') },
                ]
          }
        />
      </AppDialog>

      <AnimatePresence>
        {ratingToast && (
          <motion.div
            className="nodeRatingToast"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
          >
            {ratingToast}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isVideoRecorderOpen && (
          <motion.div
            className="nodeVideoRecorderOverlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="nodeVideoRecorderModal">
              <div className="nodeQualityBlockHeader">
                <span>{t('NLSChecklistProofVideoInspectionMemo')}</span>
                <strong>{isRecording ? t('NLSChecklistProofVoiceRecording') : t('NLSChecklistProofVideoReady')}</strong>
              </div>
              <video ref={videoPreviewRef} autoPlay muted playsInline />
              <div className="nodeVideoRecorderActions">
                <Button variant="secondary" size="sm" disabled={isRecording} loading={isRecording} onClick={startRecording}>
                  {isRecording ? t('NLSChecklistProofRecording') : t('NLSChecklistProofVideoStart15s')}
                </Button>
                <Button variant="tertiary" size="sm" onClick={closeRecorder}>
                  {t('NLSChecklistActionClose')}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
